import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Users, Settings, Play, Trophy, Target,
  Circle, Plus, Minus, RotateCcw, ChevronLeft,
  User, X, Check, Zap, ChevronUp, ChevronDown, ArrowRight, Loader2, Shield, Share2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import CoinToss from '../../components/common/CoinToss';
import { cricketGameAPI } from './services/cricketGameApi';
import { eventsAPI } from '../../services/api';
import { getStatusToPhaseMapping } from '../phaseConfig';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';

/**
 * Why a bowler came off part-way through an over, or why a batsman walked off.
 *
 * 'injury_match' is the only one that carries a consequence: it rules the player
 * out of the rest of the MATCH, not just this over or this partnership. The
 * other two leave the player available to come back on.
 */
const PLAYER_CHANGE_REASONS = [
  { value: 'injury_temporary', label: 'Injured — temporary' },
  { value: 'injury_match',     label: 'Injured — out for this game' },
  { value: 'other',            label: 'Other' }
];

/**
 * Players an injury has ruled out of the rest of the match.
 *
 * Innings-independent by design: a bowler who breaks down in the first innings
 * is no more able to bat in the second. Split out from replayPlayerEvents so the
 * restore paths, which have the raw log but not the component's derived state,
 * can ask the same question.
 */
const ruledOutFrom = (playerEvents = []) => new Set(
  playerEvents.filter(e => e.reason === 'injury_match').map(e => e.playerId)
);

/**
 * Has the side run out of batsmen?
 *
 * The plain rule is "all but one are out", because the last man cannot bat
 * alone. Somebody an injury has ruled out of the match cannot bat either, so he
 * counts against the side exactly as a wicket does — nine down and one absent
 * hurt is all out, not one wicket short of it. Without this the innings could
 * never be closed: the scorer would be asked for a batsman who does not exist.
 *
 * Batsmen who retired hurt and CAN still come back are deliberately not counted.
 * The side does have someone to send out, and whether to send him is the
 * scorer's call — that case stops for a decision rather than ending.
 */
const isAllOut = (wickets, players = [], ruledOutPlayerIds = new Set()) => {
  const absent = players.filter(p => ruledOutPlayerIds.has(p.id)).length;
  return wickets + absent >= players.length - 1;
};

/**
 * Replay the between-ball player-change log.
 *
 * Returns:
 *  - retiredBatsmanIds: batsmen currently off the field hurt, for THIS innings.
 *    A resumption cancels a retirement, so the last event for a player wins.
 *  - ruledOutPlayerIds: anyone whose injury was recorded as ending their match.
 *    Match-wide, not per-innings — a bowler who breaks down in the first innings
 *    does not walk out to bat in the second.
 *  - everRetiredIds: anyone who left the field hurt and never came back, in any
 *    innings. The scorecard needs this: a batsman is only "not out" if he was
 *    still there at the end, and this is the one case where he was not.
 *
 * Events arrive ordered by id, which is the order the scorer entered them.
 */
const replayPlayerEvents = (playerEvents = [], inningsNumber) => {
  const retiredBatsmanIds = new Set();
  const ruledOutPlayerIds = ruledOutFrom(playerEvents);
  const everRetiredIds = new Set();

  for (const e of playerEvents) {
    if (e.eventType === 'batsman_retired') everRetiredIds.add(e.playerId);
    else if (e.eventType === 'batsman_resumed') everRetiredIds.delete(e.playerId);

    if (e.inningsNumber !== inningsNumber) continue;
    if (e.eventType === 'batsman_retired') retiredBatsmanIds.add(e.playerId);
    else if (e.eventType === 'batsman_resumed') retiredBatsmanIds.delete(e.playerId);
  }

  return { retiredBatsmanIds, ruledOutPlayerIds, everRetiredIds };
};

/**
 * Cricket Scoring Component
 *
 * Props:
 * - gameCommonId: The game_common record ID
 * - gameSettings: Match settings from MatchSettingsComponent
 * - participantAInfo: Team A participant info
 * - participantBInfo: Team B participant info
 * - event: The current event object
 * - onGameComplete: function() - Called when game completes
 * - onBack: function() - Called to go back to previous step
 * - gameId: Optional specific game ID to load (from URL deep link) - for backward compatibility
 * - members: Array of members from the group - for backward compatibility
 * - onCancel: function() - for backward compatibility
 */
const CricketScoring = ({
  gameCommonId,
  gameSettings,
  participantAInfo,
  participantBInfo,
  event,
  onGameComplete,
  onBack,
  onGamePointsStatus,
  isScorer = true,
  // Centralized pause state from GameComponent — when true, hide all scoring inputs.
  paused = false,
  // Backward compatibility props
  gameId: targetGameId,
  members = [],
  onCancel
}) => {
  const { theme } = useTheme();

  // Transform members to player format (filter out deleted users)
  const transformMembers = (memberList) => {
    return (memberList || [])
      .filter(member => {
        // Filter out deleted users
        const isDeleted = member.isDeletedUser ||
                         member.is_deleted ||
                         member.status === 'deleted';
        return !isDeleted;
      })
      .map(member => ({
        id: member.userId || member.id,
        name: member.username || (member.firstName && member.lastName ? `${member.firstName} ${member.lastName}` : 'Unknown'),
        profilePicture: member.profilePicture || null
      }));
  };

  // Reusable player avatar: shows profile pic or first-letter gradient circle
  const PlayerAvatar = ({ player, size = 'sm' }) => {
    const sizeClass = size === 'xs' ? 'w-6 h-6 text-xs' : size === 'sm' ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm';
    const initial = (player?.name || '?').charAt(0).toUpperCase();
    if (player?.profilePicture) {
      return <img src={resolveImageUrl(player.profilePicture)} alt={player.name} className={`${sizeClass} rounded-full object-cover flex-shrink-0`} />;
    }
    return (
      <div className={`${sizeClass} ${theme.gradient} rounded-full flex items-center justify-center flex-shrink-0`}>
        <span className="font-bold text-white leading-none">{initial}</span>
      </div>
    );
  };

  // Shorten name for mobile display: "First Last" -> "First L"
  const shortenName = (fullName) => {
    if (!fullName) return 'Unknown';
    const parts = fullName.trim().split(' ');
    if (parts.length >= 2) {
      const firstName = parts[0];
      const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
      return `${firstName} ${lastInitial}`;
    }
    return fullName;
  };

  const formatDismissal = (dismissal) => {
    if (!dismissal) return 'out';
    const { type, fielder, bowler } = dismissal;
    const f = fielder?.name ? shortenName(fielder.name) : null;
    const b = bowler?.name ? shortenName(bowler.name) : null;
    switch (type) {
      case 'bowled':    return b ? `b ${b}` : 'Bowled';
      case 'caught':    return f && b ? `c ${f} b ${b}` : f ? `c ${f}` : 'Caught';
      case 'stumped':   return f && b ? `st ${f} b ${b}` : f ? `st ${f}` : 'Stumped';
      case 'lbw':       return b ? `lbw b ${b}` : 'LBW';
      case 'run-out':   return f ? `run out (${f})` : 'Run Out';
      case 'hit-wicket': return b ? `hit wkt b ${b}` : 'Hit Wicket';
      default:          return 'out';
    }
  };

  // Get STATUS_TO_PHASE mapping from centralized config
  const STATUS_TO_PHASE = getStatusToPhaseMapping('cricket');

  // Game and phase state - driven by database status
  const [game, setGame] = useState(null);
  const [currentPhase, setCurrentPhase] = useState('scoring'); // Start directly at scoring since teams come from props

  // Settings state - will be populated from props or loaded from existing game
  const [settings, setSettings] = useState({
    oversPerInning: gameSettings?.oversPerInning || 2,
    innings: gameSettings?.inningsPerTeam || 1,
    maxPlayersPerTeam: gameSettings?.maxPlayersPerTeam || 2,
    oversPerBowler: gameSettings?.oversPerBowler || 2
  });


  // Teams initialized from props (from GameComponent's TeamFormation step)
  const [teamA, setTeamA] = useState(() => ({
    name: participantAInfo?.name || 'Team A',
    players: participantAInfo?.players || []
  }));
  const [teamB, setTeamB] = useState(() => ({
    name: participantBInfo?.name || 'Team B',
    players: participantBInfo?.players || []
  }));
  const [availablePlayers, setAvailablePlayers] = useState(() => transformMembers(members));

  // Toss
  const [showToss, setShowToss] = useState(false);
  const [tossResult, setTossResult] = useState(null);

  // Scoring State
  const [currentInning, setCurrentInning] = useState(1);
  const [battingTeam, setBattingTeam] = useState(null);
  const [bowlingTeam, setBowlingTeam] = useState(null);
  const [innings, setInnings] = useState([]);

  // Current Over State
  const [currentOver, setCurrentOver] = useState({
    balls: [],
    striker: null,
    nonStriker: null,
    bowler: null
  });

  // Score tracking for current innings
  const [score, setScore] = useState({
    runs: 0,
    wickets: 0,
    overs: 0,
    balls: 0,
    extras: { wides: 0, noBalls: 0, byes: 0, legByes: 0 }
  });

  // Batsmen scores
  const [batsmenStats, setBatsmenStats] = useState({});

  // Bowler stats
  const [bowlerStats, setBowlerStats] = useState({});

  // Active bowlers shown in the bowling table (explicitly added or restored from stats)
  const [activeBowlers, setActiveBowlers] = useState([]);

  // Batting order - tracks the order in which batsmen will come
  const [battingOrder, setBattingOrder] = useState([]);

  // Extras modal state
  const [extrasModal, setExtrasModal] = useState({ show: false, type: null });

  // Wicket modal state
  const [wicketModal, setWicketModal] = useState({
    show: false,
    dismissalType: null,
    fielder: null,
    runOutBatsman: 'striker', // 'striker' or 'nonStriker'
    runsCompleted: 0,
    runOutEnd: null // 'striker-end' or 'bowler-end' - which end the batsman was run out at
  });

  // Expanded innings in results view
  const [expandedInnings, setExpandedInnings] = useState({});

  // Batting section expanded state (default expanded)
  const [isBattingExpanded, setIsBattingExpanded] = useState(true);
  const [isFirstInningsExpanded, setIsFirstInningsExpanded] = useState(false);
  const [isScoreboardExpanded, setIsScoreboardExpanded] = useState(true);

  // Between-ball player changes (mid-over bowler swaps, retirements, resumptions).
  // Kept in step with `game` by an effect below; appended to optimistically so
  // the card updates before the round-trip lands.
  const [playerEvents, setPlayerEvents] = useState([]);

  // Confirmation for taking a bowler off part-way through an over
  const [bowlerChangeModal, setBowlerChangeModal] = useState({
    show: false,
    incoming: null,   // the bowler coming on
    reason: '',
    note: ''
  });

  // Retire-hurt prompt. Reached from the scoring buttons rather than a batsman
  // row, so it carries which END is leaving ('striker' | 'nonStriker') and
  // resolves that to a player when it is confirmed.
  const [retireModal, setRetireModal] = useState({
    show: false,
    batsman: 'striker',
    reason: '',
    note: ''
  });

  // Track previous over's bowler (to prevent consecutive overs by same bowler)
  const [lastOverBowler, setLastOverBowler] = useState(null);
  // Keep last completed over's balls visible until new bowler is selected
  const [lastCompletedOverBalls, setLastCompletedOverBalls] = useState([]);

  // History stack for undo functionality
  const [history, setHistory] = useState([]);

  // Innings completion confirmation state
  const [inningsCompleteConfirm, setInningsCompleteConfirm] = useState({
    show: false,
    reason: null, // 'all-out', 'overs-complete', 'target-chased'
    pendingScore: null,
    pendingBatsmenStats: null,
    pendingBowlerStats: null
  });

  // API integration state
  const [matchId, setMatchId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Update available players when members prop changes
  useEffect(() => {
    if (!members || members.length === 0) return;

    // Only update if we haven't started team formation yet
    if (teamA.players.length === 0 && teamB.players.length === 0) {
      setAvailablePlayers(transformMembers(members));
    }
  }, [members]);

  // Track if initialization is in progress to prevent double calls (React StrictMode)
  const initializingRef = useRef(false);

  // Check if using modular flow (GameComponent provides gameCommonId)
  const isModularFlow = !!gameCommonId;

  // Load existing match or create new one on mount (when Start Scoring is clicked)
  useEffect(() => {

    const loadOrCreateGame = async () => {
      // Skip initialization if using modular flow (gameCommonId already provided)
      if (isModularFlow && gameCommonId) {
        // Game already created by GameComponent, just load it
        try {
          setIsLoading(true);
          const existingGame = await cricketGameAPI.getById(gameCommonId);

          setGame(existingGame);
          setMatchId(existingGame.id);

          // Restore team data from game
          // Priority: cricket-specific fields → participant_a/b_info (persisted via playerA/B) → props
          const restoredTeamA = {
            name: existingGame.teamAName || existingGame.participantAInfo?.name || participantAInfo?.name || 'Team A',
            players: existingGame.teamAPlayers || existingGame.participantAInfo?.players || participantAInfo?.players || [],
            id: 'teamA'
          };
          const restoredTeamB = {
            name: existingGame.teamBName || existingGame.participantBInfo?.name || participantBInfo?.name || 'Team B',
            players: existingGame.teamBPlayers || existingGame.participantBInfo?.players || participantBInfo?.players || [],
            id: 'teamB'
          };


          setTeamA(restoredTeamA);
          setTeamB(restoredTeamB);

          // Load settings from existing game if gameSettings prop not provided
          if (!gameSettings && existingGame.oversPerInning) {
            const loadedSettings = {
              oversPerInning: existingGame.oversPerInning || 2,
              innings: existingGame.inningsPerTeam || 1,
              maxPlayersPerTeam: existingGame.maxPlayersPerTeam || 2,
              oversPerBowler: existingGame.oversPerBowler || 2
            };
            setSettings(loadedSettings);
          }

          // Check if toss has been done and restore game state
          const hasTeamPlayers = restoredTeamA.players?.length > 0 && restoredTeamB.players?.length > 0;
          const hasBalls = existingGame.balls && existingGame.balls.length > 0;

          // Notify parent whether balls have been recorded (controls back navigation)
          if (onGamePointsStatus) onGamePointsStatus(!!hasBalls);

          // Determine batting/bowling teams based on toss (needed for both in_progress and completed)
          let firstInningsBattingTeam, firstInningsBowlingTeam;

          if (existingGame.tossWinner && existingGame.tossChoice) {
            // Has toss info - use it
            // Note: existingGame.teamAName is never written to DB in modular flow,
            // so fall back to restoredTeamA.name (which uses participantAInfo as fallback)
            const tossWinnerIsTeamA = existingGame.tossWinner === restoredTeamA.name;
            const battingFirst = existingGame.tossChoice === 'bat';

            if ((tossWinnerIsTeamA && battingFirst) || (!tossWinnerIsTeamA && !battingFirst)) {
              firstInningsBattingTeam = restoredTeamA;
              firstInningsBowlingTeam = restoredTeamB;
            } else {
              firstInningsBattingTeam = restoredTeamB;
              firstInningsBowlingTeam = restoredTeamA;
            }

            // Set toss result
            setTossResult({
              winner: { id: tossWinnerIsTeamA ? 'teamA' : 'teamB', name: existingGame.tossWinner },
              choice: battingFirst ? 'Bat First' : 'Field First'
            });
          } else {
            // No toss info - default to teamA batting (happens when balls recorded before toss saved)
            firstInningsBattingTeam = restoredTeamA;
            firstInningsBowlingTeam = restoredTeamB;
          }

          if ((existingGame.status === 'in_progress') && hasTeamPlayers &&
              (existingGame.tossWinner && existingGame.tossChoice || hasBalls)) {
            // Restore game - either has toss info OR has balls (can infer from balls)

            // Handle current innings (teams swap in 2nd innings)
            const currentInningsNumber = existingGame.currentInnings || 1;
            let currentBattingTeam, currentBowlingTeam;
            if (currentInningsNumber === 1) {
              currentBattingTeam = firstInningsBattingTeam;
              currentBowlingTeam = firstInningsBowlingTeam;
            } else {
              currentBattingTeam = firstInningsBowlingTeam;
              currentBowlingTeam = firstInningsBattingTeam;
            }

            setBattingTeam(currentBattingTeam);
            setBowlingTeam(currentBowlingTeam);
            setBattingOrder([...currentBattingTeam.players]);
            setCurrentInning(currentInningsNumber);

            // Capture bowlerStats and score from restored innings so we can use them after this block
            let restoredCurrentBowlerStats = null;
            let restoredCurrentScore = null;
            let restoredCurrentBatsmenStats = null;
            let restoredFirstInningsData = null;

            // Restore score from balls if any exist
            if (existingGame.balls && existingGame.balls.length > 0) {

              const restoredInnings = [];
              const completedInnings = [];

              // Restore innings 1 data
              const innings1 = restoreScoreFromBalls(
                existingGame.balls,
                firstInningsBattingTeam.players,
                firstInningsBowlingTeam.players,
                1
              );

              // If we're in innings 2+, innings 1 is completed - add it to completedInnings
              if (currentInningsNumber > 1 && innings1) {
                completedInnings.push({
                  battingTeam: firstInningsBattingTeam,
                  bowlingTeam: firstInningsBowlingTeam,
                  score: innings1.score,
                  batsmenStats: innings1.batsmenStats,
                  bowlerStats: innings1.bowlerStats
                });
              }

              // If in 2nd innings, restore innings 2 data
              let innings2 = null;
              if (currentInningsNumber === 2) {
                innings2 = restoreScoreFromBalls(
                  existingGame.balls,
                  firstInningsBowlingTeam.players, // Teams swap in 2nd innings
                  firstInningsBattingTeam.players,
                  2
                );
                if (innings2) {
                }
              }

              // Set ONLY completed innings (not the current one)
              setInnings(completedInnings);

              // Set current innings score and stats
              const currentInningsData = currentInningsNumber === 1 ? innings1 : innings2;
              if (currentInningsData) {
                setScore(currentInningsData.score);
                setBatsmenStats(currentInningsData.batsmenStats);
                setBowlerStats(currentInningsData.bowlerStats);
                restoredCurrentBowlerStats = currentInningsData.bowlerStats;
                restoredCurrentScore = currentInningsData.score;
                restoredCurrentBatsmenStats = currentInningsData.batsmenStats;
                restoredFirstInningsData = currentInningsNumber === 1 ? innings1 : innings1;

                // Restore current over balls
                if (currentInningsData.currentOverBalls) {
                  setCurrentOver(prev => ({
                    ...prev,
                    balls: currentInningsData.currentOverBalls
                  }));
                  if (currentInningsData.currentOverBalls.length === 0 && currentInningsData.lastOverBallsDisplay?.length > 0) {
                    setLastCompletedOverBalls(currentInningsData.lastOverBallsDisplay);
                  }
                }
              } else {
              }
            } else {
            }

            // Restore current players
            // Use parseInt on both sides to handle mixed string/number IDs from different sources
            const restoredStriker = existingGame.currentStrikerId
              ? currentBattingTeam.players.find(p => p.id === String(existingGame.currentStrikerId)) || null
              : null;
            const restoredNonStriker = existingGame.currentNonStrikerId
              ? currentBattingTeam.players.find(p => p.id === String(existingGame.currentNonStrikerId)) || null
              : null;
            const restoredBowler = existingGame.currentBowlerId
              ? currentBowlingTeam.players.find(p => p.id === String(existingGame.currentBowlerId)) || null
              : null;
            const restoredLastOverBowler = existingGame.lastOverBowlerId
              ? currentBowlingTeam.players.find(p => p.id === String(existingGame.lastOverBowlerId)) || null
              : null;

            if (restoredStriker || restoredNonStriker || restoredBowler) {
              setCurrentOver(prev => ({
                ...prev,
                striker: restoredStriker,
                nonStriker: restoredNonStriker?.id === restoredStriker?.id ? null : restoredNonStriker,
                bowler: restoredBowler
              }));
            }

            if (restoredLastOverBowler) {
              setLastOverBowler(restoredLastOverBowler);
            }

            // Restore active bowlers: those who have bowled any delivery, plus current bowler
            {
              const restoredActive = currentBowlingTeam.players.filter(
                p => restoredCurrentBowlerStats?.[p.id] !== undefined
              );
              if (restoredBowler && !restoredActive.find(p => p.id === restoredBowler.id)) {
                restoredActive.push(restoredBowler);
              }
              if (restoredActive.length > 0) {
                setActiveBowlers(restoredActive);
              }
            }

            // Check if innings/match is already complete on restore
            if (restoredCurrentScore) {
              const restoredTotalOvers = restoredCurrentScore.overs + (restoredCurrentScore.balls / 6);
              const restoredAllOut = isAllOut(
                restoredCurrentScore.wickets,
                currentBattingTeam.players,
                ruledOutFrom(existingGame.playerEvents)
              );
              // Use same effective oversPerInning as the settings state (gameSettings prop or loaded from game)
              const effectiveOversPerInning = gameSettings?.oversPerInning || existingGame.oversPerInning || 2;
              const isUnlimitedOvers = effectiveOversPerInning === -1;
              const restoredOversCompleted = !isUnlimitedOvers && restoredTotalOvers >= effectiveOversPerInning;

              const firstInningsRunsRestored = currentInningsNumber === 2 && restoredFirstInningsData
                ? restoredFirstInningsData.score.runs
                : null;
              const target = firstInningsRunsRestored !== null ? firstInningsRunsRestored + 1 : null;
              const chasedSuccessfully = target !== null && restoredCurrentScore.runs >= target;
              const matchTied = firstInningsRunsRestored !== null && (restoredAllOut || restoredOversCompleted) && restoredCurrentScore.runs === firstInningsRunsRestored;
              const targetFailed = firstInningsRunsRestored !== null && (restoredAllOut || restoredOversCompleted) && restoredCurrentScore.runs < firstInningsRunsRestored;

              if (chasedSuccessfully || matchTied || targetFailed) {
                // Match complete - show banner
                setInningsCompleteConfirm({
                  show: true,
                  reason: chasedSuccessfully ? 'target-chased' : matchTied ? 'match-tied' : 'target-failed',
                  pendingScore: restoredCurrentScore,
                  pendingBatsmenStats: restoredCurrentBatsmenStats,
                  pendingBowlerStats: restoredCurrentBowlerStats
                });
              } else if (restoredAllOut || restoredOversCompleted) {
                // Innings complete (1st innings end) - show banner
                setInningsCompleteConfirm({
                  show: true,
                  reason: restoredAllOut ? 'all-out' : 'overs-complete',
                  pendingScore: restoredCurrentScore,
                  pendingBatsmenStats: restoredCurrentBatsmenStats,
                  pendingBowlerStats: restoredCurrentBowlerStats
                });
              }
            }

            setCurrentPhase('scoring');
          } else if (existingGame.status === 'completed') {
            // Match is completed - restore innings data for results display

            // Restore all innings for display
            if (existingGame.balls && existingGame.balls.length > 0) {
              const allInnings = [];

              // Restore innings 1
              const innings1 = restoreScoreFromBalls(
                existingGame.balls,
                firstInningsBattingTeam.players,
                firstInningsBowlingTeam.players,
                1
              );
              if (innings1) {
                allInnings.push({
                  battingTeam: firstInningsBattingTeam,
                  bowlingTeam: firstInningsBowlingTeam,
                  score: innings1.score,
                  batsmenStats: innings1.batsmenStats,
                  bowlerStats: innings1.bowlerStats
                });
              }

              // Restore innings 2 if exists
              const innings2 = restoreScoreFromBalls(
                existingGame.balls,
                firstInningsBowlingTeam.players,
                firstInningsBattingTeam.players,
                2
              );
              if (innings2) {
                allInnings.push({
                  battingTeam: firstInningsBowlingTeam,
                  bowlingTeam: firstInningsBattingTeam,
                  score: innings2.score,
                  batsmenStats: innings2.batsmenStats,
                  bowlerStats: innings2.bowlerStats
                });
              }

              setInnings(allInnings);
            }

            setCurrentPhase('completed');
          } else {
            // No toss yet - show toss modal
            if (hasTeamPlayers && !existingGame.tossWinner) {
              setShowToss(true);
            }
            // Initialize teams for toss
            setBattingTeam(restoredTeamA);
            setBowlingTeam(restoredTeamB);
            if (restoredTeamA.players?.length > 0) {
              setBattingOrder([...restoredTeamA.players]);
            }
            setCurrentPhase('scoring');
          }
        } catch (error) {
          console.error('[CricketScoring] Failed to load game:', error);
          setApiError('Failed to load game. Please try again.');
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // Legacy flow (event-based) - DEPRECATED, only for backward compatibility
      // New games should use modular flow (GameComponent)
      if (!event?.id) return;
      if (initializingRef.current) return; // Prevent double initialization
      if (matchId) return; // Already have a match

      console.warn('[CricketScoring] Using legacy event-based flow - consider using GameComponent instead');

      initializingRef.current = true;
      setIsLoading(true);
      setApiError(null);

      try {
        // Check for existing matches
        const matches = await cricketGameAPI.getByEventId(event.id);

        if (matches && matches.length > 0) {
          let activeMatch;

          // If a specific gameId is provided (from deep link), load that game
          if (targetGameId) {
            activeMatch = matches.find(m => m.id === targetGameId);
          }

          // Otherwise, find an in-progress or setup match first
          if (!activeMatch) {
            const activeStatuses = ['setup', 'player_selection', 'toss', 'active', 'innings-break', 'in_progress'];
            activeMatch = matches.find(m => activeStatuses.includes(m.status));
            if (activeMatch) {
            } else {
            }
          }

          // If no active match, check for completed matches (for viewing results)
          if (!activeMatch) {
            // Get the most recent completed match
            const completedMatch = matches.find(m => m.status === 'completed');
            if (completedMatch) {
              activeMatch = completedMatch;
            }
          }

          if (activeMatch) {
            // Resume existing match
            setGame(activeMatch);
            setMatchId(activeMatch.id);

            // Determine phase from status
            const restoredPhase = STATUS_TO_PHASE[activeMatch.status] || 'teams';
            setCurrentPhase(restoredPhase);

            // Settings are now from props (gameSettings), not from database

            // Restore team names and players if available
            // Note: teamAName/teamBName may be null in modular flow — fall back to participantAInfo/BInfo
            const restoredTeamA = {
              name: activeMatch.teamAName || activeMatch.participantAInfo?.name || 'Team A',
              players: activeMatch.teamAPlayers || activeMatch.participantAInfo?.players || [],
              id: 'teamA'
            };
            const restoredTeamB = {
              name: activeMatch.teamBName || activeMatch.participantBInfo?.name || 'Team B',
              players: activeMatch.teamBPlayers || activeMatch.participantBInfo?.players || [],
              id: 'teamB'
            };
            setTeamA(restoredTeamA);
            setTeamB(restoredTeamB);

            // Determine the correct phase based on match state
            // Check if team players exist
            const hasTeamAPlayers = restoredTeamA.players && restoredTeamA.players.length > 0;
            const hasTeamBPlayers = restoredTeamB.players && restoredTeamB.players.length > 0;
            const hasTeamPlayers = hasTeamAPlayers && hasTeamBPlayers;

            if (activeMatch.status === 'in_progress') {
              // Match is in progress/active - toss was done
              if (activeMatch.tossWinner && activeMatch.tossChoice && hasTeamPlayers) {
                // Have toss info AND team players - go to scoring
                const tossWinnerIsTeamA = activeMatch.tossWinner === restoredTeamA.name;
                const battingFirst = activeMatch.tossChoice === 'bat';

                // Determine batting team based on toss using restored teams with players
                // First, determine which team batted first based on toss
                let firstInningsBattingTeam, firstInningsBowlingTeam;
                if ((tossWinnerIsTeamA && battingFirst) || (!tossWinnerIsTeamA && !battingFirst)) {
                  // Team A batted first
                  firstInningsBattingTeam = restoredTeamA;
                  firstInningsBowlingTeam = restoredTeamB;
                } else {
                  // Team B batted first
                  firstInningsBattingTeam = restoredTeamB;
                  firstInningsBowlingTeam = restoredTeamA;
                }

                // In 2nd innings, teams swap
                const currentInningsNumber = activeMatch.currentInnings || 1;
                let currentBattingTeamRestored, currentBowlingTeamRestored;
                if (currentInningsNumber === 1) {
                  currentBattingTeamRestored = firstInningsBattingTeam;
                  currentBowlingTeamRestored = firstInningsBowlingTeam;
                } else {
                  // 2nd innings: teams swap
                  currentBattingTeamRestored = firstInningsBowlingTeam;
                  currentBowlingTeamRestored = firstInningsBattingTeam;
                }

                setBattingTeam(currentBattingTeamRestored);
                setBowlingTeam(currentBowlingTeamRestored);
                setBattingOrder([...currentBattingTeamRestored.players]);

                // Set toss result
                setTossResult({
                  winner: { id: tossWinnerIsTeamA ? 'teamA' : 'teamB', name: activeMatch.tossWinner },
                  choice: battingFirst ? 'Bat First' : 'Field First'
                });

                // Restore current player selections if saved (use corrected teams based on innings)
                // Use parseInt on both sides to handle mixed string/number IDs from different sources
                const restoredStriker = activeMatch.currentStrikerId
                  ? currentBattingTeamRestored.players.find(p => p.id === String(activeMatch.currentStrikerId)) || null
                  : null;
                const restoredNonStriker = activeMatch.currentNonStrikerId
                  ? currentBattingTeamRestored.players.find(p => p.id === String(activeMatch.currentNonStrikerId)) || null
                  : null;
                const restoredBowler = activeMatch.currentBowlerId
                  ? currentBowlingTeamRestored.players.find(p => p.id === String(activeMatch.currentBowlerId)) || null
                  : null;

                if (restoredStriker || restoredNonStriker || restoredBowler) {
                  // Safety check: same player cannot be both striker and non-striker
                  const validStriker = restoredStriker;
                  const validNonStriker = (restoredNonStriker?.id === restoredStriker?.id) ? null : restoredNonStriker;

                  setCurrentOver(prev => ({
                    ...prev,
                    striker: validStriker,
                    nonStriker: validNonStriker,
                    bowler: restoredBowler
                  }));
                }

                // Always set the current innings number
                setCurrentInning(currentInningsNumber);

                // If we're in 2nd innings, restore the 1st innings data for display
                if (currentInningsNumber === 2 && activeMatch.balls && activeMatch.balls.length > 0) {
                  const innings1 = restoreScoreFromBalls(
                    activeMatch.balls,
                    firstInningsBattingTeam.players,
                    firstInningsBowlingTeam.players,
                    1
                  );
                  if (innings1) {
                    setInnings([{
                      battingTeam: firstInningsBattingTeam,
                      bowlingTeam: firstInningsBowlingTeam,
                      score: innings1.score,
                      batsmenStats: innings1.batsmenStats,
                      bowlerStats: innings1.bowlerStats
                    }]);
                  }
                }

                // Restore score and stats from saved balls
                if (activeMatch.balls && activeMatch.balls.length > 0) {
                  const restored = restoreScoreFromBalls(
                    activeMatch.balls,
                    currentBattingTeamRestored.players,
                    currentBowlingTeamRestored.players,
                    currentInningsNumber
                  );

                  if (restored) {
                    setScore(restored.score);
                    setBatsmenStats(restored.batsmenStats);
                    setBowlerStats(restored.bowlerStats);

                    // Check if restored striker/non-striker is out, if so clear them
                    const strikerIsOut = restoredStriker && restored.batsmenStats[restoredStriker.id]?.out;
                    const nonStrikerIsOut = restoredNonStriker && restored.batsmenStats[restoredNonStriker.id]?.out;

                    setCurrentOver(prev => ({
                      ...prev,
                      balls: restored.currentOverBalls,
                      striker: strikerIsOut ? null : prev.striker,
                      nonStriker: nonStrikerIsOut ? null : prev.nonStriker
                    }));
                    if (restored.currentOverBalls.length === 0 && restored.lastOverBallsDisplay?.length > 0) {
                      setLastCompletedOverBalls(restored.lastOverBallsDisplay);
                    }

                    // Restore active bowlers: those who have bowled any delivery, plus current bowler
                    const restoredActiveBowlers = currentBowlingTeamRestored.players.filter(
                      p => restored.bowlerStats[p.id] !== undefined
                    );
                    if (restoredBowler && !restoredActiveBowlers.find(p => p.id === restoredBowler.id)) {
                      restoredActiveBowlers.push(restoredBowler);
                    }
                    setActiveBowlers(restoredActiveBowlers);


                    // Check if the game/innings is already complete after restoration
                    const restoredScore = restored.score;
                    const totalOvers = restoredScore.overs + (restoredScore.balls / 6);
                    const allOut = isAllOut(
                      restoredScore.wickets,
                      currentBattingTeamRestored.players,
                      ruledOutFrom(activeMatch.playerEvents)
                    );
                    const isUnlimitedOvers = activeMatch.oversPerInning === -1;
                    const oversCompleted = !isUnlimitedOvers && totalOvers >= activeMatch.oversPerInning;

                    // For 2nd innings, get the 1st innings data to check target
                    let firstInningsData = null;
                    if (currentInningsNumber === 2) {
                      firstInningsData = restoreScoreFromBalls(
                        activeMatch.balls,
                        firstInningsBattingTeam.players,
                        firstInningsBowlingTeam.players,
                        1
                      );
                    }

                    // Check 2nd-innings result flags
                    const firstInningsRuns2 = firstInningsData ? firstInningsData.score.runs : null;
                    const target = firstInningsRuns2 !== null ? firstInningsRuns2 + 1 : null;
                    const chasedSuccessfully = target !== null && restoredScore.runs >= target;
                    const matchTied2 = firstInningsRuns2 !== null && (allOut || oversCompleted) && restoredScore.runs === firstInningsRuns2;
                    const targetFailed2 = firstInningsRuns2 !== null && (allOut || oversCompleted) && restoredScore.runs < firstInningsRuns2;

                    if (chasedSuccessfully || matchTied2 || targetFailed2) {
                      // Match is complete — show banner so user can finalise
                      // Store 1st innings data for display
                      setInnings([{
                        battingTeam: firstInningsBattingTeam,
                        bowlingTeam: firstInningsBowlingTeam,
                        score: firstInningsData.score,
                        batsmenStats: firstInningsData.batsmenStats,
                        bowlerStats: firstInningsData.bowlerStats
                      }]);
                      setInningsCompleteConfirm({
                        show: true,
                        reason: chasedSuccessfully ? 'target-chased' : matchTied2 ? 'match-tied' : 'target-failed',
                        pendingScore: restoredScore,
                        pendingBatsmenStats: restored.batsmenStats,
                        pendingBowlerStats: restored.bowlerStats
                      });
                    } else if (allOut || oversCompleted) {
                      // 1st innings complete — show banner to move to next innings
                      setInningsCompleteConfirm({
                        show: true,
                        reason: allOut ? 'all-out' : 'overs-complete',
                        pendingScore: restoredScore,
                        pendingBatsmenStats: restored.batsmenStats,
                        pendingBowlerStats: restored.bowlerStats
                      });
                    }
                  }
                }

                // Phase already set from STATUS_TO_PHASE mapping above
              } else {
                // No toss info or no team players - phase will be 'teams' from mapping
              }
            } else if (activeMatch.status === 'completed') {
              // Match completed - restore innings data and show results
              const tossWinnerIsTeamA = activeMatch.tossWinner === restoredTeamA.name;
              const battingFirst = activeMatch.tossChoice === 'bat';

              // Determine which team batted first
              let firstBattingTeam, firstBowlingTeam;
              if ((tossWinnerIsTeamA && battingFirst) || (!tossWinnerIsTeamA && !battingFirst)) {
                firstBattingTeam = restoredTeamA;
                firstBowlingTeam = restoredTeamB;
              } else {
                firstBattingTeam = restoredTeamB;
                firstBowlingTeam = restoredTeamA;
              }

              // Restore both innings from balls
              if (activeMatch.balls && activeMatch.balls.length > 0) {
                const innings1 = restoreScoreFromBalls(
                  activeMatch.balls,
                  firstBattingTeam.players,
                  firstBowlingTeam.players,
                  1
                );
                const innings2 = restoreScoreFromBalls(
                  activeMatch.balls,
                  firstBowlingTeam.players, // Teams swap for 2nd innings
                  firstBattingTeam.players,
                  2
                );

                const restoredInnings = [];
                if (innings1) {
                  restoredInnings.push({
                    battingTeam: firstBattingTeam,
                    bowlingTeam: firstBowlingTeam,
                    score: innings1.score,
                    batsmenStats: innings1.batsmenStats,
                    bowlerStats: innings1.bowlerStats
                  });
                }
                if (innings2) {
                  restoredInnings.push({
                    battingTeam: firstBowlingTeam,
                    bowlingTeam: firstBattingTeam,
                    score: innings2.score,
                    batsmenStats: innings2.batsmenStats,
                    bowlerStats: innings2.bowlerStats
                  });
                }
                setInnings(restoredInnings);
              }

              // Set toss result for display
              setTossResult({
                winner: { id: tossWinnerIsTeamA ? 'teamA' : 'teamB', name: activeMatch.tossWinner },
                choice: battingFirst ? 'Bat First' : 'Field First'
              });

              // Phase already set from STATUS_TO_PHASE mapping above
            } else {
              // Status is 'setup', 'player_selection', etc - phase already set from mapping
            }

          } else {
            // All matches completed, create a new one with status='player_selection'
            const newMatch = await cricketGameAPI.create({
              eventId: event.id,
              teamAName: 'Team A',
              teamBName: 'Team B',
              oversPerInning: settings.oversPerInning,
              oversPerBowler: settings.oversPerBowler,
              inningsPerTeam: settings.innings,
              maxPlayersPerTeam: settings.maxPlayersPerTeam,
              status: 'player_selection'
            });
            setGame(newMatch);
            setMatchId(newMatch.id);
            setCurrentPhase('teams');
          }
        } else {
          // No matches exist, create new one with status='player_selection'
          const newMatch = await cricketGameAPI.create({
            eventId: event.id,
            teamAName: 'Team A',
            teamBName: 'Team B',
            oversPerInning: settings.oversPerInning,
            oversPerBowler: settings.oversPerBowler,
            inningsPerTeam: settings.innings,
            maxPlayersPerTeam: settings.maxPlayersPerTeam,
            status: 'player_selection'
          });
          setGame(newMatch);
          setMatchId(newMatch.id);
          setCurrentPhase('teams');
        }
      } catch (error) {
        console.error('Failed to initialize match:', error);
        // If it's a duplicate key error, try to fetch the existing match
        if (error.message?.includes('Unique constraint')) {
          try {
            const matches = await cricketGameAPI.getByEventId(event.id);
            const activeStatuses = ['setup', 'player_selection', 'toss', 'active', 'innings-break', 'in_progress'];
            const activeMatch = matches?.find(m => activeStatuses.includes(m.status));
            if (activeMatch) {
              setGame(activeMatch);
              setMatchId(activeMatch.id);
              const restoredPhase = STATUS_TO_PHASE[activeMatch.status] || 'setup';
              setCurrentPhase(restoredPhase);
            }
          } catch (retryError) {
            setApiError('Failed to initialize match. Settings will be saved when you proceed.');
          }
        } else {
          setApiError('Failed to initialize match. Settings will be saved when you proceed.');
        }
      } finally {
        setIsLoading(false);
        initializingRef.current = false;
      }
    };

    loadOrCreateGame();
  }, [isModularFlow, gameCommonId, event?.id, targetGameId, participantAInfo, participantBInfo]);

  // Auto-save player selections (striker, non-striker, bowler) when they change
  useEffect(() => {
    const savePlayerSelections = async () => {
      if (!matchId) return;
      if (currentPhase !== 'scoring' && currentPhase !== 'innings-break') return;
      // Skip if nothing is selected yet — avoid hitting the API with all-null payload
      if (!currentOver.striker?.id && !currentOver.nonStriker?.id && !currentOver.bowler?.id) return;

      try {
        // Player selection state saved to game_cricket_matches
        const stateData = {
          currentStrikerId: currentOver.striker?.id || null,
          currentNonStrikerId: currentOver.nonStriker?.id || null,
          currentBowlerId: currentOver.bowler?.id || null
        };

        await cricketGameAPI.updateState(matchId, stateData);
      } catch (error) {
        console.error('Failed to auto-save player selections:', error);
      }
    };

    savePlayerSelections();
  }, [matchId, currentPhase, currentOver.striker?.id, currentOver.nonStriker?.id, currentOver.bowler?.id]);

  // Teams are now from props - no need for add/remove player functions

  // Handle toss completion
  const handleTossComplete = async (result) => {
    setTossResult(result);
    setShowToss(false);

    // Determine which actual team won based on the result
    // result.winner has { id: 'teamA' or 'teamB', name: string }
    const winnerIsTeamA = result.winner.id === 'teamA';
    const actualWinner = winnerIsTeamA ? teamA : teamB;
    const actualLoser = winnerIsTeamA ? teamB : teamA;

    let battingTeamData, bowlingTeamData;
    if (result.choice === 'Bat First') {
      battingTeamData = { ...actualWinner, id: winnerIsTeamA ? 'teamA' : 'teamB' };
      bowlingTeamData = { ...actualLoser, id: winnerIsTeamA ? 'teamB' : 'teamA' };
    } else {
      battingTeamData = { ...actualLoser, id: winnerIsTeamA ? 'teamB' : 'teamA' };
      bowlingTeamData = { ...actualWinner, id: winnerIsTeamA ? 'teamA' : 'teamB' };
    }

    setBattingTeam(battingTeamData);
    setBowlingTeam(bowlingTeamData);
    // Initialize batting order with the batting team's players
    const newBattingOrder = [...battingTeamData.players];
    setBattingOrder(newBattingOrder);

    // Don't auto-select - let user arrange batting order and select
    setCurrentOver(prev => ({
      ...prev,
      striker: null,
      nonStriker: null
    }));

    // Reset last over bowler for new innings
    setLastOverBowler(null);

    // Update match with toss result and set to in_progress
    // Always save when matchId is available (modular flow) or when event.id exists (legacy flow)
    if (matchId) {
      setIsLoading(true);
      try {
        // Match already exists, update with toss result and team players
        // Save players via playerA/playerB so they are stored in participant_a/b_info (persisted)
        const updatedMatch = await cricketGameAPI.update(matchId, {
          playerA: { type: 'team', name: teamA.name, players: teamA.players },
          playerB: { type: 'team', name: teamB.name, players: teamB.players },
          tossWinner: actualWinner.name,
          tossChoice: result.choice === 'Bat First' ? 'bat' : 'field',
          status: 'in_progress'
        });
        setGame(updatedMatch);
        setCurrentPhase('scoring');
      } catch (error) {
        console.error('Failed to save toss result:', error);
        setApiError('Failed to save toss. Scoring will continue locally.');
      } finally {
        setIsLoading(false);
      }
    } else if (event?.id) {
      // Legacy flow: no matchId yet, create the match now
      setIsLoading(true);
      try {
        const matchData = {
          eventId: event.id,
          teamAName: teamA.name,
          teamBName: teamB.name,
          teamAPlayers: teamA.players,
          teamBPlayers: teamB.players,
          oversPerInning: settings.oversPerInning,
          oversPerBowler: settings.oversPerBowler,
          inningsPerTeam: settings.innings,
          maxPlayersPerTeam: settings.maxPlayersPerTeam,
          tossWinner: actualWinner.name,
          tossChoice: result.choice === 'Bat First' ? 'bat' : 'field',
          status: 'in_progress'
        };
        const createdMatch = await cricketGameAPI.create(matchData);
        setGame(createdMatch);
        setMatchId(createdMatch.id);
        setCurrentPhase('scoring');
      } catch (error) {
        console.error('Failed to save toss result:', error);
        setApiError('Failed to save toss. Scoring will continue locally.');
      } finally {
        setIsLoading(false);
      }
    }

    // Phase will be set from database status update above
  };

  // Select top 2 from batting order as striker and non-striker
  const useTopTwoBatsmen = () => {
    const yetToBat = battingOrder.filter(p =>
      batsmenStats[p.id] === undefined &&
      currentOver.striker?.id !== p.id &&
      currentOver.nonStriker?.id !== p.id
    );
    if (yetToBat.length >= 2) {
      setCurrentOver(prev => ({ ...prev, striker: yetToBat[0], nonStriker: yetToBat[1] }));
    } else if (yetToBat.length === 1) {
      setCurrentOver(prev => ({ ...prev, striker: yetToBat[0], nonStriker: null }));
    }
  };

  // Restore score and stats from saved balls
  const restoreScoreFromBalls = (balls, battingTeamPlayers, bowlingTeamPlayers, inningsNumber = 1) => {

    if (!balls || balls.length === 0) {
      return null;
    }

    // Log first few balls to see their structure

    // Filter balls for current innings
    const inningsBalls = balls.filter(b => b.inningsNumber === inningsNumber);

    if (inningsBalls.length === 0) {
      return null;
    }

    // Initialize stats
    const restoredScore = { runs: 0, wickets: 0, overs: 0, balls: 0, extras: { wides: 0, noBalls: 0, byes: 0, legByes: 0 } };
    const restoredBatsmenStats = {};
    const restoredBowlerStats = {};
    const currentOverBalls = [];

    // Find the current over number (1-indexed in DB)
    const lastBall = inningsBalls[inningsBalls.length - 1];
    const lastOverNumber = lastBall?.overNumber || 1;
    // If the last over had 6 legal deliveries it is complete — the current over is a new
    // (empty) one. This matters on refresh when a new bowler was selected but hasn't
    // bowled yet: the last ball still belongs to the finished over, so currentOverBalls
    // must be empty rather than showing the previous over's balls.
    const legalBallsInLastOver = inningsBalls.filter(
      b => b.overNumber === lastOverNumber && b.isLegalDelivery
    ).length;
    const currentOverNumber = legalBallsInLastOver >= 6 ? lastOverNumber + 1 : lastOverNumber;

    // Process each ball
    inningsBalls.forEach(ball => {
      // Count runs
      const totalRuns = (ball.strikerRuns || 0) + (ball.extraRuns || 0);
      restoredScore.runs += totalRuns;

      // Count extras
      if (ball.isWide) restoredScore.extras.wides += ball.extraRuns || 1;
      if (ball.isNoBall) restoredScore.extras.noBalls += ball.extraRuns || 1;
      if (ball.isBye) restoredScore.extras.byes += ball.extraRuns || 0;
      if (ball.isLegBye) restoredScore.extras.legByes += ball.extraRuns || 0;

      // Count wickets
      if (ball.isWicket) restoredScore.wickets += 1;

      // Update batsman stats
      if (ball.strikerId) {
        if (!restoredBatsmenStats[ball.strikerId]) {
          restoredBatsmenStats[ball.strikerId] = { runs: 0, balls: 0, fours: 0, sixes: 0, out: false };
        }
        restoredBatsmenStats[ball.strikerId].runs += ball.strikerRuns || 0;
        if (ball.isLegalDelivery) {
          restoredBatsmenStats[ball.strikerId].balls += 1;
        }
        if (ball.isFour) restoredBatsmenStats[ball.strikerId].fours += 1;
        if (ball.isSix) restoredBatsmenStats[ball.strikerId].sixes += 1;
      }

      // Mark batsman out
      if (ball.isWicket && ball.wicketBatsmanId) {
        if (!restoredBatsmenStats[ball.wicketBatsmanId]) {
          restoredBatsmenStats[ball.wicketBatsmanId] = { runs: 0, balls: 0, fours: 0, sixes: 0, out: false };
        }
        restoredBatsmenStats[ball.wicketBatsmanId].out = true;
        // Look up the fielder from bowling team players to get the full object with name
        const fielder = ball.fielderId
          ? bowlingTeamPlayers.find(p => p.id === String(ball.fielderId)) || { id: ball.fielderId, name: null }
          : null;
        const bowlerObj = ball.bowlerId
          ? bowlingTeamPlayers.find(p => p.id === String(ball.bowlerId)) || { id: ball.bowlerId, name: null }
          : null;
        restoredBatsmenStats[ball.wicketBatsmanId].dismissal = {
          type: ball.dismissalType,
          fielder: fielder,
          bowler: bowlerObj
        };
      }

      // Update bowler stats
      if (ball.bowlerId) {
        if (!restoredBowlerStats[ball.bowlerId]) {
          restoredBowlerStats[ball.bowlerId] = { overs: 0, balls: 0, runs: 0, wickets: 0, wides: 0, noBalls: 0 };
        }
        // Runs conceded (striker runs + extras except byes/leg byes)
        let runsConceded = ball.strikerRuns || 0;
        if (ball.isWide || ball.isNoBall) runsConceded += ball.extraRuns || 0;
        restoredBowlerStats[ball.bowlerId].runs += runsConceded;

        if (ball.isLegalDelivery) {
          restoredBowlerStats[ball.bowlerId].balls += 1;
        }
        if (ball.isWide) restoredBowlerStats[ball.bowlerId].wides += 1;
        if (ball.isNoBall) restoredBowlerStats[ball.bowlerId].noBalls += 1;
        if (ball.isWicket && ball.dismissalType !== 'run-out') {
          restoredBowlerStats[ball.bowlerId].wickets += 1;
        }
      }

      // Build current over balls (balls from the current over number)
      if (ball.overNumber === currentOverNumber) {
        // Map ball to the format used in currentOver.balls
        let outcome = 'runs';
        let runs = ball.strikerRuns || 0;

        if (ball.isWicket) outcome = 'wicket-detail';
        else if (ball.isFour) outcome = 'four';
        else if (ball.isSix) outcome = 'six';
        else if (ball.isWide) { outcome = 'wide'; runs = ball.extraRuns || 0; }
        else if (ball.isNoBall) { outcome = ball.strikerRuns > 0 ? 'no-ball-bat' : 'no-ball-extra'; runs = ball.strikerRuns || ball.extraRuns || 0; }
        else if (ball.isBye) { outcome = 'bye'; runs = ball.extraRuns || 0; }
        else if (ball.isLegBye) { outcome = 'leg-bye'; runs = ball.extraRuns || 0; }
        else if (ball.strikerRuns === 0) outcome = 'dot';

        currentOverBalls.push({
          outcome,
          runs,
          strikerId: ball.strikerId,
          bowlerId: ball.bowlerId
        });
      }
    });

    // Calculate completed overs and balls
    const totalLegalBalls = inningsBalls.filter(b => b.isLegalDelivery).length;
    restoredScore.overs = Math.floor(totalLegalBalls / 6);
    restoredScore.balls = totalLegalBalls % 6;

    // Calculate maiden overs: completed overs (6 legal balls) with 0 runs conceded by bowler
    const overBowlerMap = {};
    inningsBalls.forEach(ball => {
      if (!ball.bowlerId || !ball.overNumber) return;
      const key = `${ball.bowlerId}_${ball.overNumber}`;
      if (!overBowlerMap[key]) {
        overBowlerMap[key] = { bowlerId: ball.bowlerId, legalBalls: 0, isMaiden: true };
      }
      if (ball.isLegalDelivery) overBowlerMap[key].legalBalls++;
      if (ball.isWide || ball.isNoBall || (ball.strikerRuns || 0) > 0) {
        overBowlerMap[key].isMaiden = false;
      }
    });
    Object.values(overBowlerMap).forEach(({ bowlerId, legalBalls, isMaiden }) => {
      if (legalBalls === 6 && isMaiden && restoredBowlerStats[bowlerId]) {
        restoredBowlerStats[bowlerId].maidens = (restoredBowlerStats[bowlerId].maidens || 0) + 1;
      }
    });

    // Convert bowler balls to overs
    Object.keys(restoredBowlerStats).forEach(bowlerId => {
      const stats = restoredBowlerStats[bowlerId];
      stats.overs = Math.floor(stats.balls / 6);
      stats.ballsInOver = stats.balls % 6;
    });

    // Find the bowler of the last COMPLETED over (for consecutive-over restriction).
    // This is the over just before the current (possibly partial) one.
    const lastCompletedOverNumber = currentOverNumber - 1;
    const lastCompletedOverDbBalls = lastCompletedOverNumber > 0
      ? inningsBalls.filter(b => b.overNumber === lastCompletedOverNumber)
      : [];
    const lastCompletedOverBowlerId = lastCompletedOverDbBalls.length > 0
      ? lastCompletedOverDbBalls[lastCompletedOverDbBalls.length - 1]?.bowlerId ?? null
      : null;

    // Map last completed over DB balls to display format (shown until next bowler selected)
    const lastOverBallsDisplay = currentOverBalls.length === 0
      ? lastCompletedOverDbBalls.map(ball => {
          let outcome = 'runs';
          let runs = ball.strikerRuns || 0;
          if (ball.isWicket) outcome = 'wicket-detail';
          else if (ball.isFour) outcome = 'four';
          else if (ball.isSix) outcome = 'six';
          else if (ball.isWide) { outcome = 'wide'; runs = ball.extraRuns || 0; }
          else if (ball.isNoBall) { outcome = ball.strikerRuns > 0 ? 'no-ball-bat' : 'no-ball-extra'; runs = ball.strikerRuns || ball.extraRuns || 0; }
          else if (ball.isBye) { outcome = 'bye'; runs = ball.extraRuns || 0; }
          else if (ball.isLegBye) { outcome = 'leg-bye'; runs = ball.extraRuns || 0; }
          else if (ball.strikerRuns === 0) outcome = 'dot';
          return { outcome, runs, strikerId: ball.strikerId, bowlerId: ball.bowlerId };
        })
      : [];

    return {
      score: restoredScore,
      batsmenStats: restoredBatsmenStats,
      bowlerStats: restoredBowlerStats,
      currentOverBalls,
      lastOverBallsDisplay,
      lastCompletedOverBowlerId
    };
  };

  // ── Between-ball player changes ────────────────────────────────────────────

  // Every load path calls setGame with the full game, so the log rides along
  // with it rather than needing a fetch of its own.
  useEffect(() => {
    if (game?.playerEvents) setPlayerEvents(game.playerEvents);
  }, [game]);

  const { retiredBatsmanIds, ruledOutPlayerIds, everRetiredIds } = useMemo(
    () => replayPlayerEvents(playerEvents, currentInning),
    [playerEvents, currentInning]
  );

  // ── Who is left to bat ─────────────────────────────────────────────────────
  //
  // Anyone not out, not already at the crease, and not ruled out of the match.
  // Split by whether they can just walk out (`freshBatsmen`) or have to be
  // brought back from a retirement first (`returnableBatsmen`).
  const availableToBat = (battingTeam?.players || []).filter(p =>
    !batsmenStats[p.id]?.out &&
    !ruledOutPlayerIds.has(p.id) &&
    currentOver.striker?.id !== p.id &&
    currentOver.nonStriker?.id !== p.id
  );
  const returnableBatsmen = availableToBat.filter(p => retiredBatsmanIds.has(p.id));
  const freshBatsmen = availableToBat.filter(p => !retiredBatsmanIds.has(p.id));

  /**
   * A wicket has fallen, nobody is left to walk out, and the only players who
   * could fill the gap are ones who retired hurt.
   *
   * This is a decision, not a state the app can settle on its own: the side may
   * send an injured man back out, or it may declare itself done. Without asking,
   * the scorer is simply stuck — the scoring buttons need two batsmen and there
   * is no second batsman to pick.
   */
  const onlyRetiredLeft =
    !inningsCompleteConfirm.show &&
    Boolean(battingTeam) &&
    (!currentOver.striker || !currentOver.nonStriker) &&
    freshBatsmen.length === 0 &&
    returnableBatsmen.length > 0;

  /**
   * Close the innings where it stands.
   *
   * Nothing is committed here — this raises the same confirmation banner an
   * innings reaches on its own, and the scorer still has to press through it
   * (or undo) before anything is recorded.
   *
   * 'all-out' when the side genuinely had nobody left to send out; the default
   * when the scorer is closing it early for a reason the app cannot see — a
   * declaration, a side that has to leave, players who never turned up.
   */
  const endInningsNow = (reason = 'innings-closed') => {
    setInningsCompleteConfirm({
      show: true,
      reason,
      pendingScore: score,
      pendingBatsmenStats: batsmenStats,
      pendingBowlerStats: bowlerStats
    });
  };

  // An over is under way once its bowler has sent down anything at all — a wide
  // counts, so this reads the ball list rather than score.balls, which only
  // moves on legal deliveries.
  const isOverInProgress = Boolean(currentOver.bowler) && currentOver.balls.length > 0;

  /**
   * POST a player change and fold the result back into local state.
   * Appends optimistically so the UI does not wait on the round-trip, and
   * reconciles from the server's copy of the game when it lands.
   */
  const recordPlayerEvent = async (eventData) => {
    const optimistic = {
      id: `pending-${Date.now()}`,
      inningsNumber: eventData.inningsNumber,
      eventType: eventData.eventType,
      playerId: eventData.playerId,
      replacementPlayerId: eventData.replacementPlayerId || null,
      reason: eventData.reason,
      reasonNote: eventData.reasonNote || null,
      overNumber: eventData.overNumber ?? null,
      ballInOver: eventData.ballInOver ?? null
    };
    setPlayerEvents(prev => [...prev, optimistic]);

    if (!matchId) return;

    try {
      const result = await cricketGameAPI.recordPlayerEvent(matchId, eventData);
      if (result?.game) setGame(result.game);
    } catch (error) {
      console.error('[CricketScoring] recordPlayerEvent failed:', error);
      // Drop the optimistic row again — leaving it would show a retirement that
      // will vanish on the next reload, which is worse than showing the failure.
      setPlayerEvents(prev => prev.filter(e => e.id !== optimistic.id));
      toast.error('Could not save the player change. Please try again.');
      throw error;
    }
  };

  /**
   * Bowler tapped in the bowling table. Swapping bowlers between overs is
   * routine and goes straight through; doing it part-way through one is not, so
   * it stops for a confirmation and a reason first.
   */
  const handleBowlerSelect = (player, canSelect) => {
    if (!isScorer || !canSelect) return;

    if (isOverInProgress && currentOver.bowler?.id !== player.id) {
      setBowlerChangeModal({ show: true, incoming: player, reason: '', note: '' });
      return;
    }

    setLastCompletedOverBalls([]);
    setCurrentOver(prev => ({ ...prev, bowler: player }));
  };

  const confirmBowlerChange = async () => {
    const { incoming, reason, note } = bowlerChangeModal;
    const outgoing = currentOver.bowler;
    if (!incoming || !outgoing || !reason) return;

    try {
      await recordPlayerEvent({
        eventType: 'bowler_change',
        playerId: outgoing.id,
        replacementPlayerId: incoming.id,
        reason,
        reasonNote: reason === 'other' ? note.trim() : undefined,
        inningsNumber: currentInning,
        // score.overs counts COMPLETED overs, so the one in progress is the next
        // one along. Matches the 1-indexed over_number on game_cricket_balls.
        overNumber: score.overs + 1,
        ballInOver: score.balls
      });
    } catch {
      return; // recordPlayerEvent has already surfaced the failure
    }

    setLastCompletedOverBalls([]);
    setCurrentOver(prev => ({ ...prev, bowler: incoming }));
    setActiveBowlers(prev => (prev.some(p => p.id === incoming.id) ? prev : [...prev, incoming]));
    setBowlerChangeModal({ show: false, incoming: null, reason: '', note: '' });
  };

  /**
   * Retire the batsman hurt. Not a dismissal: the innings stays open, no wicket
   * is recorded, and no bowler is credited. Unless the injury was logged as
   * ending their match, they can be sent back out later.
   */
  const confirmRetire = async () => {
    const { batsman, reason, note } = retireModal;
    const player = batsman === 'nonStriker' ? currentOver.nonStriker : currentOver.striker;
    if (!player || !reason) return;

    try {
      await recordPlayerEvent({
        eventType: 'batsman_retired',
        playerId: player.id,
        reason,
        reasonNote: reason === 'other' ? note.trim() : undefined,
        inningsNumber: currentInning,
        overNumber: score.overs + 1,
        ballInOver: score.balls
      });
    } catch {
      return;
    }

    setCurrentOver(prev => ({
      ...prev,
      striker:    prev.striker?.id    === player.id ? null : prev.striker,
      nonStriker: prev.nonStriker?.id === player.id ? null : prev.nonStriker
    }));
    setRetireModal({ show: false, batsman: 'striker', reason: '', note: '' });
  };

  /**
   * Send a batsman to the crease. A player coming back from a retirement needs
   * that logged too, otherwise a reload replays the retirement and walks them
   * straight back off.
   */
  const handleBatsmanSelect = async (player) => {
    if (!isScorer) return;
    if (currentOver.striker && currentOver.nonStriker) return;

    if (retiredBatsmanIds.has(player.id)) {
      if (ruledOutPlayerIds.has(player.id)) return;
      try {
        await recordPlayerEvent({
          eventType: 'batsman_resumed',
          playerId: player.id,
          reason: 'resumed',
          inningsNumber: currentInning,
          overNumber: score.overs + 1,
          ballInOver: score.balls
        });
      } catch {
        return;
      }
    }

    setCurrentOver(prev => (
      !prev.striker      ? { ...prev, striker: player }
      : !prev.nonStriker ? { ...prev, nonStriker: player }
      : prev
    ));
  };

  // Record a ball outcome
  const recordBall = (outcome, runs = 0) => {
    // Notify parent that points have been recorded (disable back navigation)
    if (history.length === 0 && onGamePointsStatus) onGamePointsStatus(true);

    // Save current state to history before making changes
    setHistory(prev => [...prev, {
      score: { ...score, extras: { ...score.extras } },
      batsmenStats: JSON.parse(JSON.stringify(batsmenStats)),
      bowlerStats: JSON.parse(JSON.stringify(bowlerStats)),
      currentOver: { ...currentOver, balls: [...currentOver.balls] },
      lastOverBowler
    }]);

    const newBall = {
      outcome,
      runs,
      striker: currentOver.striker,
      bowler: currentOver.bowler,
      timestamp: new Date().toISOString()
    };

    let updatedScore = { ...score, extras: { ...score.extras } };
    let ballCounts = true; // Whether this ball counts towards the over

    // Create synchronous copies of stats to track updates (for handleInningsComplete)
    let syncedBatsmenStats = JSON.parse(JSON.stringify(batsmenStats));
    let syncedBowlerStats = JSON.parse(JSON.stringify(bowlerStats));

    // Helper to update batsman stats both sync and async
    const updateBatsmanStat = (playerId, updates) => {
      if (!playerId) return;
      syncedBatsmenStats[playerId] = {
        ...syncedBatsmenStats[playerId],
        ...updates,
        runs: (syncedBatsmenStats[playerId]?.runs || 0) + (updates.addRuns || 0),
        balls: (syncedBatsmenStats[playerId]?.balls || 0) + (updates.addBalls || 0),
        fours: (syncedBatsmenStats[playerId]?.fours || 0) + (updates.addFours || 0),
        sixes: (syncedBatsmenStats[playerId]?.sixes || 0) + (updates.addSixes || 0)
      };
      // Remove the add* helper props
      delete syncedBatsmenStats[playerId].addRuns;
      delete syncedBatsmenStats[playerId].addBalls;
      delete syncedBatsmenStats[playerId].addFours;
      delete syncedBatsmenStats[playerId].addSixes;
    };

    switch (outcome) {
      case 'runs':
        updatedScore.runs += runs;
        // Update batsman stats
        if (currentOver.striker) {
          updateBatsmanStat(currentOver.striker.id, { addRuns: runs, addBalls: 1 });
          setBatsmenStats(prev => ({
            ...prev,
            [currentOver.striker.id]: {
              ...prev[currentOver.striker.id],
              runs: (prev[currentOver.striker.id]?.runs || 0) + runs,
              balls: (prev[currentOver.striker.id]?.balls || 0) + 1
            }
          }));
        }
        // Rotate strike on odd runs
        if (runs % 2 === 1) {
          setCurrentOver(prev => ({
            ...prev,
            striker: prev.nonStriker,
            nonStriker: prev.striker
          }));
        }
        break;

      case 'dot':
        if (currentOver.striker) {
          updateBatsmanStat(currentOver.striker.id, { addBalls: 1 });
          setBatsmenStats(prev => ({
            ...prev,
            [currentOver.striker.id]: {
              ...prev[currentOver.striker.id],
              balls: (prev[currentOver.striker.id]?.balls || 0) + 1
            }
          }));
        }
        break;

      case 'wide':
        // Wide: 1 base + additional runs (all as extras/wides)
        updatedScore.runs += 1 + runs;
        updatedScore.extras.wides += 1 + runs;
        ballCounts = false;
        // Rotate strike on odd additional runs
        if (runs % 2 === 1) {
          setCurrentOver(prev => ({
            ...prev,
            striker: prev.nonStriker,
            nonStriker: prev.striker
          }));
        }
        break;

      case 'no-ball-bat':
        // No ball where batsman hit: 1 extra + runs credited to batsman
        updatedScore.runs += 1 + runs;
        updatedScore.extras.noBalls += 1;
        if (currentOver.striker && runs > 0) {
          updateBatsmanStat(currentOver.striker.id, {
            addRuns: runs,
            addBalls: 1,
            addFours: runs === 4 ? 1 : 0,
            addSixes: runs === 6 ? 1 : 0
          });
          setBatsmenStats(prev => ({
            ...prev,
            [currentOver.striker.id]: {
              ...prev[currentOver.striker.id],
              runs: (prev[currentOver.striker.id]?.runs || 0) + runs,
              balls: (prev[currentOver.striker.id]?.balls || 0) + 1,
              fours: (prev[currentOver.striker.id]?.fours || 0) + (runs === 4 ? 1 : 0),
              sixes: (prev[currentOver.striker.id]?.sixes || 0) + (runs === 6 ? 1 : 0)
            }
          }));
        }
        ballCounts = false;
        // Rotate strike on odd runs
        if (runs % 2 === 1) {
          setCurrentOver(prev => ({
            ...prev,
            striker: prev.nonStriker,
            nonStriker: prev.striker
          }));
        }
        break;

      case 'no-ball-extra':
        // No ball where batsman didn't hit: 1 base + runs as extras
        updatedScore.runs += 1 + runs;
        updatedScore.extras.noBalls += 1 + runs;
        ballCounts = false;
        // Rotate strike on odd runs
        if (runs % 2 === 1) {
          setCurrentOver(prev => ({
            ...prev,
            striker: prev.nonStriker,
            nonStriker: prev.striker
          }));
        }
        break;

      case 'bye':
        updatedScore.runs += runs;
        updatedScore.extras.byes += runs;
        if (runs % 2 === 1) {
          setCurrentOver(prev => ({
            ...prev,
            striker: prev.nonStriker,
            nonStriker: prev.striker
          }));
        }
        break;

      case 'leg-bye':
        updatedScore.runs += runs;
        updatedScore.extras.legByes += runs;
        if (runs % 2 === 1) {
          setCurrentOver(prev => ({
            ...prev,
            striker: prev.nonStriker,
            nonStriker: prev.striker
          }));
        }
        break;

      case 'wicket':
        // This is now handled by wicket-detail, but keeping for backward compatibility
        updatedScore.wickets += 1;
        if (currentOver.striker) {
          updateBatsmanStat(currentOver.striker.id, { addBalls: 1, out: true, dismissal: { type: 'unknown' } });
          setBatsmenStats(prev => ({
            ...prev,
            [currentOver.striker.id]: {
              ...prev[currentOver.striker.id],
              balls: (prev[currentOver.striker.id]?.balls || 0) + 1,
              out: true,
              dismissal: { type: 'unknown' }
            }
          }));
          setCurrentOver(prev => ({ ...prev, striker: null }));
        }
        break;

      case 'wicket-detail':
        // Detailed wicket with dismissal info
        // runs parameter contains: { dismissalType, fielder, runOutBatsman, runsCompleted }
        const wicketInfo = runs;
        updatedScore.wickets += 1;

        if (wicketInfo.dismissalType === 'run-out') {
          // Run out: add completed runs to score
          updatedScore.runs += wicketInfo.runsCompleted || 0;

          const outBatsman = wicketInfo.runOutBatsman === 'striker'
            ? currentOver.striker
            : currentOver.nonStriker;

          if (outBatsman) {
            const addBallsForRunOut = wicketInfo.runOutBatsman === 'striker' ? 1 : 0;
            updateBatsmanStat(outBatsman.id, {
              addBalls: addBallsForRunOut,
              out: true,
              dismissal: {
                type: 'run-out',
                fielder: wicketInfo.fielder,
                runsCompleted: wicketInfo.runsCompleted
              }
            });
            setBatsmenStats(prev => ({
              ...prev,
              [outBatsman.id]: {
                ...prev[outBatsman.id],
                // Only add ball faced if striker got out (they faced the ball)
                balls: wicketInfo.runOutBatsman === 'striker'
                  ? (prev[outBatsman.id]?.balls || 0) + 1
                  : (prev[outBatsman.id]?.balls || 0),
                out: true,
                dismissal: {
                  type: 'run-out',
                  fielder: wicketInfo.fielder,
                  runsCompleted: wicketInfo.runsCompleted
                }
              }
            }));
          }

          // Handle strike rotation for run out based on which END the batsman was out at
          // runOutEnd: 'striker-end' = batsman was out at striker's crease
          // runOutEnd: 'bowler-end' = batsman was out at bowler's/non-striker's crease
          const runOutEnd = wicketInfo.runOutEnd || 'striker-end';

          // Determine the surviving batsman
          const survivingBatsman = wicketInfo.runOutBatsman === 'striker'
            ? currentOver.nonStriker
            : currentOver.striker;

          if (runOutEnd === 'striker-end') {
            // Run out at striker's end: new batsman becomes striker, surviving batsman becomes non-striker
            setCurrentOver(prev => ({
              ...prev,
              striker: null,
              nonStriker: survivingBatsman
            }));
          } else {
            // Run out at bowler's end: new batsman becomes non-striker, surviving batsman becomes striker
            setCurrentOver(prev => ({
              ...prev,
              striker: survivingBatsman,
              nonStriker: null
            }));
          }
        } else {
          // Other dismissals: bowled, caught, stumped, lbw, hit-wicket
          // Striker is always out, ball counts
          if (currentOver.striker) {
            updateBatsmanStat(currentOver.striker.id, {
              addBalls: 1,
              out: true,
              dismissal: {
                type: wicketInfo.dismissalType,
                fielder: wicketInfo.fielder,
                bowler: currentOver.bowler
              }
            });
            setBatsmenStats(prev => ({
              ...prev,
              [currentOver.striker.id]: {
                ...prev[currentOver.striker.id],
                balls: (prev[currentOver.striker.id]?.balls || 0) + 1,
                out: true,
                dismissal: {
                  type: wicketInfo.dismissalType,
                  fielder: wicketInfo.fielder,
                  bowler: currentOver.bowler
                }
              }
            }));
            setCurrentOver(prev => ({ ...prev, striker: null }));
          }
        }
        break;

      case 'four':
        updatedScore.runs += 4;
        if (currentOver.striker) {
          updateBatsmanStat(currentOver.striker.id, { addRuns: 4, addBalls: 1, addFours: 1 });
          setBatsmenStats(prev => ({
            ...prev,
            [currentOver.striker.id]: {
              ...prev[currentOver.striker.id],
              runs: (prev[currentOver.striker.id]?.runs || 0) + 4,
              balls: (prev[currentOver.striker.id]?.balls || 0) + 1,
              fours: (prev[currentOver.striker.id]?.fours || 0) + 1
            }
          }));
        }
        break;

      case 'six':
        updatedScore.runs += 6;
        if (currentOver.striker) {
          updateBatsmanStat(currentOver.striker.id, { addRuns: 6, addBalls: 1, addSixes: 1 });
          setBatsmenStats(prev => ({
            ...prev,
            [currentOver.striker.id]: {
              ...prev[currentOver.striker.id],
              runs: (prev[currentOver.striker.id]?.runs || 0) + 6,
              balls: (prev[currentOver.striker.id]?.balls || 0) + 1,
              sixes: (prev[currentOver.striker.id]?.sixes || 0) + 1
            }
          }));
        }
        break;
    }

    // Update bowler stats (sync and async)
    if (currentOver.bowler && ballCounts) {
      const bowlerId = currentOver.bowler.id;
      const runsToAdd = outcome === 'wicket' || outcome === 'wicket-detail' ? 0 : (typeof runs === 'number' ? runs : 0);
      const wicketsToAdd = (outcome === 'wicket' || outcome === 'wicket-detail') ? 1 : 0;

      // Sync update
      syncedBowlerStats[bowlerId] = {
        ...syncedBowlerStats[bowlerId],
        balls: (syncedBowlerStats[bowlerId]?.balls || 0) + 1,
        runs: (syncedBowlerStats[bowlerId]?.runs || 0) + runsToAdd,
        wickets: (syncedBowlerStats[bowlerId]?.wickets || 0) + wicketsToAdd
      };

      // Async update
      setBowlerStats(prev => ({
        ...prev,
        [bowlerId]: {
          ...prev[bowlerId],
          balls: (prev[bowlerId]?.balls || 0) + 1,
          runs: (prev[bowlerId]?.runs || 0) + runsToAdd,
          wickets: (prev[bowlerId]?.wickets || 0) + wicketsToAdd
        }
      }));
    }

    // Update ball count
    if (ballCounts) {
      const newBalls = updatedScore.balls + 1;
      if (newBalls === 6) {
        // Check if this was a maiden over (no wides, no no-balls, no runs off bat)
        if (currentOver.bowler) {
          const allOverBalls = [...currentOver.balls, newBall];
          const isMaiden = allOverBalls.every(b =>
            b.outcome !== 'wide' &&
            b.outcome !== 'no-ball-bat' &&
            b.outcome !== 'no-ball-extra' &&
            b.outcome !== 'runs' &&
            b.outcome !== 'four' &&
            b.outcome !== 'six'
          );
          if (isMaiden) {
            const mBowlerId = currentOver.bowler.id;
            syncedBowlerStats[mBowlerId] = {
              ...syncedBowlerStats[mBowlerId],
              maidens: (syncedBowlerStats[mBowlerId]?.maidens || 0) + 1
            };
            setBowlerStats(prev => ({
              ...prev,
              [mBowlerId]: { ...prev[mBowlerId], maidens: (prev[mBowlerId]?.maidens || 0) + 1 }
            }));
          }
        }
        updatedScore.overs += 1;
        updatedScore.balls = 0;
        // Check if innings ends on this ball (so we can keep balls for display)
        const isUnlimitedOversCheck = settings.oversPerInning === -1;
        const oversEndsInnings = !isUnlimitedOversCheck && updatedScore.overs >= settings.oversPerInning;
        const allOutCheck = isAllOut(updatedScore.wickets, battingTeam.players, ruledOutPlayerIds);
        const inningsEndsOnThisBall = oversEndsInnings || allOutCheck;
        // Save current bowler as last over's bowler (to prevent consecutive overs)
        setLastOverBowler(currentOver.bowler);
        // Rotate strike at end of over and clear bowler for next over selection.
        // If innings ends, keep balls and bowler visible — no next over to set up.
        if (!inningsEndsOnThisBall) {
          setLastCompletedOverBalls([...currentOver.balls, newBall]);
        }
        setCurrentOver(prev => ({
          ...prev,
          balls: inningsEndsOnThisBall ? [...prev.balls, newBall] : [],
          striker: prev.nonStriker,
          nonStriker: prev.striker,
          bowler: inningsEndsOnThisBall ? prev.bowler : null
        }));
      } else {
        updatedScore.balls = newBalls;
        setCurrentOver(prev => ({
          ...prev,
          balls: [...prev.balls, newBall]
        }));
      }
    } else {
      setCurrentOver(prev => ({
        ...prev,
        balls: [...prev.balls, newBall]
      }));
    }

    setScore(updatedScore);

    // Check for innings completion
    const totalOvers = updatedScore.overs + (updatedScore.balls / 6);
    const allOut = isAllOut(updatedScore.wickets, battingTeam.players, ruledOutPlayerIds);
    const isUnlimitedOvers = settings.oversPerInning === -1;
    const oversCompleted = !isUnlimitedOvers && totalOvers >= settings.oversPerInning;

    // Check 2nd-innings result flags (only meaningful when a target exists)
    const firstInningsRuns = (currentInning > 1 && innings.length > 0) ? innings[0].score.runs : null;
    const target = firstInningsRuns !== null ? firstInningsRuns + 1 : null;
    // Win: chasing team strictly surpasses 1st innings score
    const chasedSuccessfully = target !== null && updatedScore.runs >= target;
    // Tie: overs exhausted / all out with exactly equal scores
    const matchTied = firstInningsRuns !== null && (oversCompleted || allOut) && updatedScore.runs === firstInningsRuns;
    // Loss: overs exhausted / all out with strictly fewer runs than 1st innings
    const targetFailed = firstInningsRuns !== null && (oversCompleted || allOut) && updatedScore.runs < firstInningsRuns;

    // Debug logging

    if (oversCompleted || allOut || chasedSuccessfully || matchTied || targetFailed) {
      // Show confirmation modal instead of immediately proceeding
      const reason = chasedSuccessfully ? 'target-chased'
        : matchTied    ? 'match-tied'
        : targetFailed ? 'target-failed'
        : allOut       ? 'all-out'
        : 'overs-complete';
      setInningsCompleteConfirm({
        show: true,
        reason,
        pendingScore: updatedScore,
        pendingBatsmenStats: syncedBatsmenStats,
        pendingBowlerStats: syncedBowlerStats
      });

      // Match completion is saved only when the user explicitly clicks Finish (handleInningsComplete).
    }

    // Call API to persist ball (async, non-blocking)
    if (matchId) {
      const isWicket = outcome === 'wicket' || outcome === 'wicket-detail';
      const wicketInfo = outcome === 'wicket-detail' ? runs : null;

      // Compute new player state to persist in DB — mirrors setCurrentOver logic so
      // state can be correctly restored when the page is refreshed mid-game.
      let newStrikerId = currentOver.striker?.id ?? null;
      let newNonStrikerId = currentOver.nonStriker?.id ?? null;
      let newBowlerId = currentOver.bowler?.id ?? null;
      let newLastOverBowlerId = lastOverBowler?.id ?? null;

      if (isWicket) {
        if (wicketInfo?.dismissalType === 'run-out') {
          const runOutEnd = wicketInfo.runOutEnd || 'striker-end';
          if (runOutEnd === 'striker-end') {
            newStrikerId = null; // striker out, nonStriker survives
          } else {
            newNonStrikerId = null; // non-striker out, striker survives
          }
        } else {
          newStrikerId = null; // all other dismissals: striker out
        }
      } else {
        // Strike rotation on odd runs (matches per-case setCurrentOver calls)
        const strikeRunsForRotation = ['runs', 'wide', 'no-ball-bat', 'no-ball-extra', 'bye', 'leg-bye'].includes(outcome)
          ? (typeof runs === 'number' ? runs : 0)
          : 0;
        if (strikeRunsForRotation % 2 === 1) {
          [newStrikerId, newNonStrikerId] = [newNonStrikerId, newStrikerId];
        }
      }

      // End-of-over: rotate strike and (conditionally) clear bowler
      const overWillEnd = ballCounts && (score.balls + 1) === 6;
      if (overWillEnd) {
        [newStrikerId, newNonStrikerId] = [newNonStrikerId, newStrikerId];
        newLastOverBowlerId = newBowlerId;
        const newOversAfterBall = score.overs + 1;
        const isUnlimitedOversLocal = settings.oversPerInning === -1;
        const oversEndsInnings = !isUnlimitedOversLocal && newOversAfterBall >= settings.oversPerInning;
        const allOutLocal = isAllOut(updatedScore.wickets, battingTeam.players, ruledOutPlayerIds);
        const inningsEndsOnThisBall = oversEndsInnings || allOutLocal;
        if (!inningsEndsOnThisBall) {
          newBowlerId = null; // clear bowler for next over selection
        }
      }

      // Build ball data for API
      const ballApiData = {
        inningsNumber: currentInning,
        overNumber: score.overs + 1, // 1-indexed (1st over = 1, not 0)
        ballInOver: ballCounts ? score.balls + 1 : score.balls,
        bowlerId: currentOver.bowler?.id,
        strikerId: currentOver.striker?.id,
        nonStrikerId: currentOver.nonStriker?.id,
        isWide: outcome === 'wide',
        isNoBall: outcome === 'no-ball-bat' || outcome === 'no-ball-extra',
        isBye: outcome === 'bye',
        isLegBye: outcome === 'leg-bye',
        isLegalDelivery: ballCounts,
        strikerRuns: ['runs', 'four', 'six', 'no-ball-bat'].includes(outcome)
          ? (outcome === 'four' ? 4 : outcome === 'six' ? 6 : (typeof runs === 'number' ? runs : 0))
          : 0,
        extraRuns: ['wide', 'no-ball-extra', 'bye', 'leg-bye'].includes(outcome)
          ? (outcome === 'wide' ? 1 + (runs || 0) : outcome === 'no-ball-extra' ? 1 + (runs || 0) : (runs || 0))
          : (outcome === 'no-ball-bat' ? 1 : 0),
        isFour: outcome === 'four' || (outcome === 'no-ball-bat' && runs === 4),
        isSix: outcome === 'six' || (outcome === 'no-ball-bat' && runs === 6),
        isWicket: isWicket,
        wicketBatsmanId: isWicket ? (wicketInfo?.runOutBatsman === 'nonStriker' ? currentOver.nonStriker?.id : currentOver.striker?.id) : null,
        dismissalType: wicketInfo?.dismissalType || (outcome === 'wicket' ? 'unknown' : null),
        fielderId: wicketInfo?.fielder?.id || null,
        // New player state — backend persists these so state can be restored on refresh
        newStrikerId,
        newNonStrikerId,
        newBowlerId,
        lastOverBowlerId: newLastOverBowlerId
      };

      cricketGameAPI.recordBall(matchId, ballApiData)
        .catch(error => {
          console.error('Failed to record ball:', error);
          setApiError('Failed to save ball. Continue scoring - data will sync later.');
        });
    }
  };

  // Undo last ball
  const undoLastBall = async () => {
    // Clear innings complete banner first (so scoring buttons reappear)
    if (inningsCompleteConfirm.show) {
      setInningsCompleteConfirm({
        show: false,
        reason: null,
        pendingScore: null,
        pendingBatsmenStats: null,
        pendingBowlerStats: null
      });
    }

    if (history.length > 0) {
      // Live case: restore from local history
      const lastState = history[history.length - 1];
      setScore(lastState.score);
      setBatsmenStats(lastState.batsmenStats);
      setBowlerStats(lastState.bowlerStats);
      setCurrentOver(lastState.currentOver);
      setLastOverBowler(lastState.lastOverBowler);
      setHistory(prev => prev.slice(0, -1));

      if (history.length === 1 && onGamePointsStatus) onGamePointsStatus(false);
    }

    // Always call API to delete the last ball from the database
    if (matchId) {
      try {
        const updatedGame = await cricketGameAPI.undoLastBall(matchId);

        // Refresh case (history was empty): restore full state from API response
        if (history.length === 0 && updatedGame?.balls) {
          const currentInningsNumber = updatedGame.currentInnings || currentInning;
          // updatedGame.teamAName is never written in modular flow — use teamA state (already restored)
          const tossWinnerIsTeamA = updatedGame.tossWinner === (teamA?.name || updatedGame.teamAName);
          const battingFirst = updatedGame.tossChoice === 'bat';
          let ftBatting, ftBowling;
          if ((tossWinnerIsTeamA && battingFirst) || (!tossWinnerIsTeamA && !battingFirst)) {
            ftBatting = teamA; ftBowling = teamB;
          } else {
            ftBatting = teamB; ftBowling = teamA;
          }
          const curBatting = currentInningsNumber === 1 ? ftBatting : ftBowling;
          const curBowling = currentInningsNumber === 1 ? ftBowling : ftBatting;

          const restored = restoreScoreFromBalls(
            updatedGame.balls,
            curBatting.players,
            curBowling.players,
            currentInningsNumber
          );

          if (restored) {
            setScore(restored.score);
            setBatsmenStats(restored.batsmenStats);
            setBowlerStats(restored.bowlerStats);

            // Restore currentOver: balls + striker/nonStriker/bowler from DB state
            const restoredBowler = updatedGame.currentBowlerId
              ? curBowling.players.find(p => p.id === String(updatedGame.currentBowlerId)) || null
              : null;
            const restoredStriker = updatedGame.currentStrikerId
              ? curBatting.players.find(p => p.id === String(updatedGame.currentStrikerId)) || null
              : null;
            const restoredNonStriker = updatedGame.currentNonStrikerId
              ? curBatting.players.find(p => p.id === String(updatedGame.currentNonStrikerId)) || null
              : null;
            setCurrentOver(prev => ({
              ...prev,
              balls: restored.currentOverBalls || [],
              bowler: restoredBowler,
              striker: restoredStriker,
              nonStriker: restoredNonStriker
            }));

            // Restore last over bowler (controls consecutive-over restriction).
            // Derive from balls data rather than updatedGame.lastOverBowlerId because
            // the DB field is not rolled back by undo and may be stale.
            const restoredLastOverBowler = restored.lastCompletedOverBowlerId
              ? curBowling.players.find(p => p.id === String(restored.lastCompletedOverBowlerId)) || null
              : null;
            setLastOverBowler(restoredLastOverBowler);

            // Restore active bowlers
            const restoredActive = curBowling.players.filter(
              p => restored.bowlerStats[p.id] !== undefined
            );
            if (restoredActive.length > 0) setActiveBowlers(restoredActive);
          }

          if (updatedGame.balls.length === 0 && onGamePointsStatus) onGamePointsStatus(false);
        }
      } catch (error) {
        console.error('Failed to undo ball in API:', error);
        setApiError('Failed to undo last ball.');
      }
    }
  };

  // Handle innings completion
  // Accept optional parameters for synchronized values (to avoid stale state issues)
  const handleInningsComplete = (syncedScore = null, syncedBatsmenStats = null, syncedBowlerStats = null) => {
    // Use synced values if provided (from recordBall), otherwise fall back to state
    const finalScore = syncedScore || score;
    const finalBatsmenStats = syncedBatsmenStats || batsmenStats;
    const finalBowlerStats = syncedBowlerStats || bowlerStats;

    const completedInnings = {
      battingTeam: battingTeam,
      bowlingTeam: bowlingTeam,
      score: { ...finalScore },
      batsmenStats: { ...finalBatsmenStats },
      bowlerStats: { ...finalBowlerStats }
    };

    // We need to include this innings in our calculation
    const allInnings = [...innings, completedInnings];

    if (currentInning < settings.innings * 2) {
      setInnings(allInnings);

      // Switch teams
      const newBattingTeam = bowlingTeam;
      const newBowlingTeam = battingTeam;
      setBattingTeam(newBattingTeam);
      setBowlingTeam(newBowlingTeam);

      // Reset for new innings
      setScore({
        runs: 0,
        wickets: 0,
        overs: 0,
        balls: 0,
        extras: { wides: 0, noBalls: 0, byes: 0, legByes: 0 }
      });
      setBatsmenStats({});
      setBowlerStats({});
      setActiveBowlers([]);
      // Set batting order for new batting team
      const newBattingOrder = [...newBattingTeam.players];
      setBattingOrder(newBattingOrder);
      // Don't auto-select - let user arrange batting order and select for new innings
      setCurrentOver({
        balls: [],
        striker: null,
        nonStriker: null,
        bowler: null
      });
      // Reset last over bowler for new innings
      setLastOverBowler(null);
      setCurrentInning(prev => prev + 1);

      // Update match state in API for innings break
      if (matchId) {
        cricketGameAPI.updateState(matchId, {
          currentInnings: currentInning + 1,
          currentStrikerId: null,
          currentNonStrikerId: null,
          currentBowlerId: null,
          lastOverBowlerId: null,
          status: 'in_progress'  // Use standard status; UI phase managed locally
        })
          .then(updatedMatch => {
            setGame(updatedMatch);
            setCurrentPhase('innings-break');  // UI phase for innings break screen
          })
          .catch(error => console.error('Failed to update match state:', error));
      } else {
        // No matchId yet, set phase locally
        setCurrentPhase('innings-break');
      }
    } else {
      setInnings(allInnings);

      // Calculate winner and margin for API
      if (matchId && allInnings.length >= 2) {
        const firstInnings = allInnings[0];
        const secondInnings = allInnings[1];

        let winner = null;
        let marginText = '';

        // Use actual team size for wickets calculation
        const secondInningsTeamSize = secondInnings.battingTeam?.players?.length || settings.maxPlayersPerTeam;
        const wicketsRemaining = (secondInningsTeamSize - 1) - secondInnings.score.wickets;

        if (secondInnings.score.runs > firstInnings.score.runs) {
          // Chasing team won
          winner = secondInnings.battingTeam?.id === teamA.id ? 'A' : 'B';
          marginText = `by ${wicketsRemaining} wicket${wicketsRemaining !== 1 ? 's' : ''}`;
        } else if (firstInnings.score.runs > secondInnings.score.runs) {
          // First batting team won — target failed
          winner = firstInnings.battingTeam?.id === teamA.id ? 'A' : 'B';
          const runMargin = firstInnings.score.runs - secondInnings.score.runs;
          marginText = `by ${runMargin} run${runMargin !== 1 ? 's' : ''}`;
        } else {
          marginText = 'Match Tied';
        }

        // Call API to complete match (but NOT the event - let user complete it manually)
        cricketGameAPI.complete(matchId, {
          winner,
          marginText
        })
          .then(completedMatch => {
            setGame(completedMatch);
            setCurrentPhase('completed');
          })
          .catch(error => console.error('Failed to complete match in API:', error));
      } else {
        // No matchId, set phase locally
        setCurrentPhase('completed');
      }
    }
  };

  // Generate scorecard text for sharing

  // Team formation is now handled by GameComponent - teams come from props

  // Render Scoring Phase
  const renderScoring = () => {
    const isUnlimitedOvers = settings.oversPerInning === -1;
    const target = innings.length > 0 ? innings[0].score.runs + 1 : null;
    const runsNeeded = target ? target - score.runs : null;
    const ballsRemaining = isUnlimitedOvers ? null : (settings.oversPerInning * 6) - (score.overs * 6 + score.balls);

    // Calculate total extras
    const totalExtras = (score.extras.wides || 0) + (score.extras.noBalls || 0) +
                        (score.extras.byes || 0) + (score.extras.legByes || 0);

    return (
      <div className="space-y-3">
        {/* Match Info Bar - Always visible */}
        <div className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 rounded-lg ${theme.backgroundSecondary} border ${theme.border}`}>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className={theme.textMuted}>
              <span className="font-medium">Overs:</span> {isUnlimitedOvers ? '∞' : settings.oversPerInning}
            </span>
            <span className={theme.textMuted}>
              <span className="font-medium">Per Bowler:</span> {settings.oversPerBowler}
            </span>
            <span className={theme.textMuted}>
              <span className="font-medium">Innings:</span> {currentInning}/{settings.innings * 2}
            </span>
          </div>
          {target && (
            <span className={`text-xs font-medium ${
              inningsCompleteConfirm.reason === 'match-tied'    ? 'text-blue-400' :
              inningsCompleteConfirm.reason === 'target-failed' ? 'text-orange-400' :
              runsNeeded <= 0 ? 'text-green-500' : 'text-yellow-500'
            }`}>
              {inningsCompleteConfirm.reason === 'match-tied' ? (
                <>Match Tied!</>
              ) : inningsCompleteConfirm.reason === 'target-failed' ? (() => {
                const firstInningsScore = innings[0]?.score?.runs ?? 0;
                const secondInningsScore = inningsCompleteConfirm.pendingScore?.runs ?? score.runs;
                const runMargin = firstInningsScore - secondInningsScore;
                const winnerName = innings[0]?.battingTeam?.name || 'Team 1';
                return <>{winnerName} won by {runMargin} run{runMargin !== 1 ? 's' : ''}</>;
              })() : runsNeeded <= 0 ? (
                <>Target: {target} | {battingTeam?.name || 'Batting Team'} won!</>
              ) : (
                <>Target: {target} | Need {runsNeeded}{ballsRemaining !== null ? ` from ${ballsRemaining} balls` : ''}</>
              )}
            </span>
          )}
        </div>

        {/* 1st Innings Summary - Collapsible (shown only in 2nd innings) */}
        {innings.length > 0 && currentInning === 2 && (
          <div className={`${theme.card} rounded-xl border-2 ${theme.border} overflow-hidden`}>
            <button
              type="button"
              onClick={() => setIsFirstInningsExpanded(!isFirstInningsExpanded)}
              className={`w-full p-4 flex items-center justify-between ${theme.backgroundSecondary} hover:opacity-90 transition-all`}
            >
              <div className="flex items-center gap-x-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-amber-500" />
                </div>
                <div className="text-left">
                  <p className={`text-xs ${theme.textMuted}`}>1st Innings</p>
                  <p className={`font-semibold ${theme.text}`}>{innings[0].battingTeam?.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-x-3">
                <div className="text-right">
                  <p className={`text-xl font-bold ${theme.text}`}>
                    {innings[0].score.runs}/{innings[0].score.wickets}
                  </p>
                  <p className={`text-xs ${theme.textMuted}`}>
                    ({innings[0].score.overs}.{innings[0].score.balls} ov)
                  </p>
                </div>
                <ChevronDown className={`w-5 h-5 ${theme.textMuted} transition-transform ${isFirstInningsExpanded ? 'rotate-180' : ''}`} />
              </div>
            </button>

            {isFirstInningsExpanded && (
              <div className={`border-t ${theme.border} p-3 space-y-4`}>
                {/* Batting Stats */}
                <div>
                  <p className={`text-xs font-medium ${theme.textMuted} mb-2`}>Batting</p>
                  {renderInningsBattingStats(innings[0])}
                </div>

                {/* Bowling Stats */}
                <div>
                  <p className={`text-xs font-medium ${theme.textMuted} mb-2`}>Bowling ({innings[0].bowlingTeam?.name})</p>
                  {renderInningsBowlingStats(innings[0])}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Scoreboard - Collapsible wrapper for Batting, Bowling & This Over */}
        <div className={`${theme.card} rounded-xl border-2 ${theme.border} overflow-hidden`}>
          {/* Collapsible Header */}
          <button
            type="button"
            onClick={() => setIsScoreboardExpanded(!isScoreboardExpanded)}
            className={`w-full p-3 flex items-center justify-between ${theme.backgroundSecondary} hover:opacity-90 transition-all`}
          >
            <span className={`font-semibold text-sm ${theme.text}`}>
              {currentInning === 1 ? '1st' : currentInning === 2 ? '2nd' : `${currentInning}th`} Innings
            </span>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className={`text-lg font-bold ${theme.text}`}>{score.runs}/{score.wickets}</p>
                <p className={`text-xs ${theme.textMuted}`}>({score.overs}.{score.balls} ov)</p>
              </div>
              <ChevronDown className={`w-4 h-4 ${theme.textMuted} transition-transform ${isScoreboardExpanded ? 'rotate-180' : ''}`} />
            </div>
          </button>

          {isScoreboardExpanded && (
            <div className={`border-t ${theme.border} space-y-3 p-3`}>

        {/* Batting Section - Collapsible */}
        <div className={`${theme.card} rounded-xl border-2 ${theme.border} overflow-hidden`}>
          {/* Header - Clickable */}
          <button
            type="button"
            onClick={() => setIsBattingExpanded(!isBattingExpanded)}
            className={`w-full p-4 flex items-center justify-between ${theme.backgroundSecondary} hover:opacity-90 transition-all`}
          >
            <div className="flex items-center gap-x-3">
              <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-green-500" />
              </div>
              <div className="text-left">
                <p className={`font-semibold ${theme.text}`}>{battingTeam?.name} Batting</p>
                {(!currentOver.striker || !currentOver.nonStriker) && (
                  <p className="text-xs text-yellow-500">Select batsmen</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-x-3">
              <div className="text-right">
                <p className={`text-xl font-bold ${theme.text}`}>
                  {score.runs}/{score.wickets}
                </p>
                <p className={`text-xs ${theme.textMuted}`}>
                  ({score.overs}.{score.balls} ov)
                </p>
              </div>
              <ChevronDown className={`w-5 h-5 ${theme.textMuted} transition-transform ${isBattingExpanded ? 'rotate-180' : ''}`} />
            </div>
          </button>

          {/* Expanded Content */}
          {isBattingExpanded && (
            <div className={`border-t ${theme.border} p-4`}>
              <div className="space-y-1">
            {(() => {
              // Show only batsmen who have batted or are currently at the crease
              // A batsman who retired before facing a ball has no stats row yet,
              // so he is listed by the retirement itself — otherwise he would
              // drop back into "yet to bat" and lose the retired-hurt marker.
              const activeBatsmen = battingOrder.filter(p =>
                batsmenStats[p.id] !== undefined ||
                retiredBatsmanIds.has(p.id) ||
                currentOver.striker?.id === p.id ||
                currentOver.nonStriker?.id === p.id
              );
              const yetToBat = battingOrder.filter(p =>
                batsmenStats[p.id] === undefined &&
                !retiredBatsmanIds.has(p.id) &&
                currentOver.striker?.id !== p.id &&
                currentOver.nonStriker?.id !== p.id
              );
              const matchOver = inningsCompleteConfirm.show;
              const noBallsBowled = score.overs === 0 && score.balls === 0;

              return (
                <>
                  {isScorer && activeBatsmen.length === 0 && (
                    <p className={`text-xs ${theme.textMuted} text-center py-2`}>
                      Add batsmen below to begin
                    </p>
                  )}
                  {activeBatsmen.map(player => {
                    const isOut = batsmenStats[player.id]?.out;
                    const isStriker = currentOver.striker?.id === player.id;
                    const isNonStriker = currentOver.nonStriker?.id === player.id;
                    const isBatting = isStriker || isNonStriker;
                    const isRetired = retiredBatsmanIds.has(player.id);
                    // Retired hurt is not out — he can be sent back out, unless
                    // the injury was recorded as ending his match.
                    const isRuledOut = ruledOutPlayerIds.has(player.id);
                    const canSelect = !matchOver && !isOut && !isBatting && !isRuledOut &&
                      (!currentOver.striker || !currentOver.nonStriker);
                    const stats = batsmenStats[player.id];
                    const canDeselect = !matchOver && isBatting && noBallsBowled;

                    return (
                      <div
                        key={player.id}
                        className={`flex items-center justify-between p-2 rounded-lg border-2 ${
                          isOut ? `${theme.backgroundSecondary} opacity-50 border-red-500` :
                          isRetired ? `${theme.backgroundSecondary} border-amber-500` :
                          isBatting ? `${theme.backgroundSecondary} border-green-500 ${isScorer && canDeselect ? 'cursor-pointer' : ''}` :
                          (isScorer && canSelect) ? `${theme.backgroundSecondary} cursor-pointer border-transparent hover:border-green-400` :
                          `${theme.backgroundSecondary} border-transparent`
                        }`}
                        onClick={() => {
                          if (!isScorer) return;
                          if (canSelect) {
                            handleBatsmanSelect(player);
                          } else if (canDeselect) {
                            if (isStriker) {
                              setCurrentOver(prev => ({ ...prev, striker: null }));
                            } else if (isNonStriker) {
                              setCurrentOver(prev => ({ ...prev, nonStriker: null }));
                            }
                          }
                        }}
                      >
                        <div className="flex items-center gap-x-2">
                          <PlayerAvatar player={player} size="xs" />
                          <span className={`text-sm ${isOut ? 'line-through text-red-400' : theme.text}`}>
                            {shortenName(player.name)}
                          </span>
                          {isRetired && (
                            <span className="text-xs text-amber-500 italic">
                              {isRuledOut ? 'retired hurt — out of match' : 'retired hurt'}
                            </span>
                          )}
                          {isStriker && (
                            <span className={`text-xs bg-green-500 text-white px-1.5 py-0.5 rounded flex items-center gap-x-1 ${isScorer && canDeselect ? 'hover:bg-green-600' : ''}`}>
                              <span>*</span>
                              {isScorer && canDeselect && <X className="w-3 h-3" />}
                            </span>
                          )}
                          {isNonStriker && (
                            <span className={`text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded flex items-center gap-x-1 ${isScorer && canDeselect ? 'hover:bg-blue-600' : ''}`}>
                              <span>NS</span>
                              {isScorer && canDeselect && <X className="w-3 h-3" />}
                            </span>
                          )}
                          {isOut && (
                            <span className="text-xs text-red-400 italic">
                              {formatDismissal(stats?.dismissal)}
                            </span>
                          )}
                        </div>
                        <span className={`text-xs ${theme.textMuted}`}>
                          {stats?.runs ?? 0}({stats?.balls ?? 0})
                        </span>
                      </div>
                    );
                  })}

                  {/* Add Batsman — yet-to-bat players shown as selectable chips */}
                  {!matchOver && yetToBat.length > 0 && (
                    <div className={`mt-2 p-3 rounded-lg ${theme.backgroundSecondary} border ${theme.border}`}>
                      <p className={`text-xs font-medium ${theme.textMuted} mb-2`}>Add Batsman:</p>
                      <div className="flex flex-wrap gap-2">
                        {yetToBat.filter(p => !ruledOutPlayerIds.has(p.id)).map(player => {
                          const canAdd = !currentOver.striker || !currentOver.nonStriker;
                          return (
                            <button
                              key={player.id}
                              type="button"
                              onClick={() => handleBatsmanSelect(player)}
                              disabled={!canAdd || !isScorer}
                              className={`text-xs px-2.5 py-1 rounded-lg border ${theme.border} ${theme.text} ${
                                canAdd && isScorer ? `${theme.cardHover} cursor-pointer` : 'opacity-40 cursor-not-allowed'
                              } transition-colors`}
                            >
                              {shortenName(player.name)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

                {/* Extras Row */}
                {totalExtras > 0 && (
                  <div className={`flex items-center justify-between p-2 rounded-lg ${theme.backgroundSecondary}`}>
                    <div className="flex items-center gap-x-2">
                      <span className={`text-xs font-medium ${theme.textMuted} w-5`}></span>
                      <span className={`text-sm ${theme.textMuted}`}>Extras</span>
                      <span className={`text-xs ${theme.textMuted}`}>
                        (Wd {score.extras.wides}, NB {score.extras.noBalls}, B {score.extras.byes}, LB {score.extras.legByes})
                      </span>
                    </div>
                    <span className={`text-sm font-medium ${theme.text}`}>{totalExtras}</span>
                  </div>
                )}

                {/* Total Row */}
                <div className={`flex items-center justify-between p-2 rounded-lg ${theme.backgroundSecondary} border-t-2 ${theme.border} mt-2`}>
                  <div className="flex items-center gap-x-2">
                    <span className={`text-xs font-medium ${theme.textMuted} w-5`}></span>
                    <span className={`text-sm font-bold ${theme.text}`}>Total</span>
                    <span className={`text-xs ${theme.textMuted}`}>({score.overs}.{score.balls} overs)</span>
                  </div>
                  <span className={`text-lg font-bold ${theme.text}`}>{score.runs}/{score.wickets}</span>
                </div>

                {/* Did Not Bat - shown when innings is complete */}
                {inningsCompleteConfirm.show && (() => {
                  const didNotBat = battingOrder.filter(p => {
                    const s = batsmenStats[p.id];
                    return !s || (s.balls === 0 && s.runs === 0 && !s.out);
                  });
                  if (didNotBat.length === 0) return null;
                  return (
                    <div className={`p-2 border-t ${theme.border} text-xs ${theme.textMuted}`}>
                      <span className="font-medium">Did not bat: </span>
                      {didNotBat.map(p => shortenName(p.name)).join(', ')}
                    </div>
                  );
                })()}
              </div>

              {/* Select Batsmen Message */}
              {(!currentOver.striker || !currentOver.nonStriker) ? (
                <div className="mt-3 space-y-2">
                  {isScorer && (
                    <p className={`text-xs ${theme.textMuted} text-center`}>
                      {!currentOver.striker && !currentOver.nonStriker
                        ? 'Select two batsmen from "Add Batsman" below, or use "Use Top 2".'
                        : !currentOver.striker
                          ? 'Select striker from the list above or add a new batsman.'
                          : 'Select non-striker from the list above or add a new batsman.'}
                    </p>
                  )}
                  {isScorer && !currentOver.striker && !currentOver.nonStriker && (() => {
                    const yetToBat = battingOrder.filter(p =>
                      batsmenStats[p.id] === undefined &&
                      currentOver.striker?.id !== p.id &&
                      currentOver.nonStriker?.id !== p.id
                    );
                    if (yetToBat.length < 2) return null;
                    return (
                      <button
                        type="button"
                        onClick={useTopTwoBatsmen}
                        className={`w-full py-2 ${theme.buttonPrimary} rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-x-2`}
                      >
                        <Play className="w-4 h-4" />
                        <span>Use Top 2 ({shortenName(yetToBat[0]?.name)} & {shortenName(yetToBat[1]?.name)})</span>
                      </button>
                    );
                  })()}
                  {/* The scorer is being asked for a batsman. The app cannot
                      know that the ones left on the roster are not actually
                      here — somebody went home, somebody never turned up,
                      somebody is hurt and not coming back — so being asked is
                      a dead end unless there is also a way out of it. */}
                  {isScorer && !onlyRetiredLeft && !inningsCompleteConfirm.show &&
                   (score.overs > 0 || score.balls > 0 || score.wickets > 0) && (
                    <button
                      type="button"
                      onClick={() => endInningsNow()}
                      className={`w-full py-2 rounded-lg border ${theme.border} ${theme.textMuted} text-sm font-medium ${theme.cardHover} transition-colors`}
                    >
                      End innings here
                    </button>
                  )}
                </div>
              ) : isScorer && (score.overs === 0 && score.balls === 0) && (
                <p className={`text-xs ${theme.textMuted} text-center mt-2`}>
                  Click on a batsman to change selection before first ball
                </p>
              )}
            </div>
          )}
        </div>

        {/* This Over */}
        <div className={`${theme.card} rounded-xl p-4 border ${theme.border}`}>
          <div className="flex items-center justify-between mb-2">
            <h4 className={`text-xs font-medium ${theme.textMuted}`}>
              {currentOver.bowler ? shortenName(currentOver.bowler.name) : lastCompletedOverBalls.length > 0 ? 'Last Over' : 'This Over'}
            </h4>
            {isScorer && !paused && (history.length > 0 || inningsCompleteConfirm.show || score.overs > 0 || score.balls > 0) && (
              <button
                type="button"
                onClick={() => {
                  if (inningsCompleteConfirm.show) {
                    setInningsCompleteConfirm({ show: false, reason: null, pendingScore: null, pendingBatsmenStats: null, pendingBowlerStats: null });
                  }
                  undoLastBall();
                }}
                className={`flex items-center gap-x-1 px-2 py-1 rounded text-xs font-medium ${theme.backgroundSecondary} ${theme.text} hover:bg-orange-500 hover:text-white transition-colors border ${theme.border}`}
              >
                <RotateCcw className="w-3 h-3" />
                <span>Undo</span>
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {(currentOver.balls.length > 0 ? currentOver.balls : lastCompletedOverBalls).map((ball, idx) => {
              const isNoBall = ball.outcome === 'no-ball-bat' || ball.outcome === 'no-ball-extra';
              const isWide = ball.outcome === 'wide';
              const isBye = ball.outcome === 'bye';
              const isLegBye = ball.outcome === 'leg-bye';
              const isExtra = isNoBall || isWide || isBye || isLegBye;

              return (
                <div
                  key={idx}
                  className={`min-w-8 h-8 px-1 rounded-full flex items-center justify-center text-xs font-bold ${
                    (ball.outcome === 'wicket' || ball.outcome === 'wicket-detail') ? 'bg-red-500 text-white' :
                    ball.outcome === 'four' ? 'bg-blue-500 text-white' :
                    ball.outcome === 'six' ? 'bg-purple-500 text-white' :
                    isWide || isNoBall ? 'bg-yellow-500 text-white' :
                    isBye || isLegBye ? 'bg-gray-500 text-white' :
                    `${theme.backgroundSecondary} ${theme.text} border border-gray-400`
                  }`}
                >
                  {(ball.outcome === 'wicket' || ball.outcome === 'wicket-detail') ? 'W' :
                   isWide ? (ball.runs > 0 ? `Wd+${ball.runs}` : 'Wd') :
                   isNoBall ? (ball.runs > 0 ? `NB+${ball.runs}` : 'NB') :
                   isBye ? `B${ball.runs}` :
                   isLegBye ? `LB${ball.runs}` :
                   ball.runs}
                </div>
              );
            })}
            {[...Array(Math.max(0, 6 - (currentOver.balls.length > 0 ? currentOver.balls : lastCompletedOverBalls).filter(b =>
              b.outcome !== 'wide' && b.outcome !== 'no-ball-bat' && b.outcome !== 'no-ball-extra'
            ).length))].map((_, idx) => (
              <div key={`empty-${idx}`} className={`w-8 h-8 rounded-full border-2 border-dashed ${theme.border}`} />
            ))}
          </div>
        </div>

        {/* Scoring Buttons - Hidden for viewers, when innings/match is complete, and when paused */}
        {/* Nobody left to walk out — the side either recalls someone or is done */}
        {isScorer && onlyRetiredLeft && !paused && (
          <div className={`${theme.card} rounded-xl border-2 border-amber-500 p-4 space-y-3`}>
            <div className="flex items-center gap-x-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className={`font-semibold ${theme.text}`}>No batsman left to come in</p>
                <p className={`text-xs ${theme.textMuted}`}>
                  {returnableBatsmen.length === 1
                    ? `Only ${shortenName(returnableBatsmen[0].name)}, retired hurt, is left. Send him back out or end the innings.`
                    : 'Only retired batsmen are left. Send one back out or end the innings.'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {returnableBatsmen.map(player => (
                <button
                  key={player.id}
                  type="button"
                  onClick={() => handleBatsmanSelect(player)}
                  className="px-3 py-2 bg-amber-500 text-white rounded-xl text-sm font-medium hover:bg-amber-600 transition-colors"
                >
                  Resume {shortenName(player.name)}
                </button>
              ))}
              <button
                type="button"
                onClick={() => endInningsNow('all-out')}
                className={`px-3 py-2 rounded-xl border ${theme.border} ${theme.text} text-sm font-medium ${theme.cardHover} transition-colors`}
              >
                End innings
              </button>
            </div>
          </div>
        )}

        {isScorer && currentOver.striker && currentOver.nonStriker && currentOver.bowler && !inningsCompleteConfirm.show && !paused && (
          <div className="space-y-3">
            {/* Run Buttons */}
            <div className="grid grid-cols-7 gap-2">
              {[0, 1, 2, 3, 4, 5, 6].map(runs => (
                <button
                  key={runs}
                  onClick={() => recordBall(runs === 0 ? 'dot' : 'runs', runs)}
                  className={`py-4 rounded-xl font-bold text-lg transition-colors ${
                    runs === 4 ? 'bg-blue-500 text-white' :
                    runs === 6 ? 'bg-purple-500 text-white' :
                    `${theme.backgroundSecondary} ${theme.text} ${theme.cardHover}`
                  }`}
                >
                  {runs}
                </button>
              ))}
            </div>

            {/* Boundary Buttons */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => recordBall('four', 4)}
                className="py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-colors flex items-center justify-center gap-x-2"
              >
                <Zap className="w-5 h-5" />
                <span>FOUR</span>
              </button>
              <button
                onClick={() => recordBall('six', 6)}
                className="py-3 bg-purple-500 text-white rounded-xl font-semibold hover:bg-purple-600 transition-colors flex items-center justify-center gap-x-2"
              >
                <Zap className="w-5 h-5" />
                <span>SIX</span>
              </button>
            </div>

            {/* Extras, Wicket & Retire */}
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setExtrasModal({ show: true, type: 'wide' })}
                className="py-2 bg-yellow-500 text-white rounded-xl text-sm font-medium hover:bg-yellow-600 transition-colors"
              >
                Wide
              </button>
              <button
                onClick={() => setExtrasModal({ show: true, type: 'no-ball' })}
                className="py-2 bg-yellow-500 text-white rounded-xl text-sm font-medium hover:bg-yellow-600 transition-colors"
              >
                No Ball
              </button>
              <button
                onClick={() => setExtrasModal({ show: true, type: 'bye' })}
                className="py-2 bg-gray-400 text-white rounded-xl text-sm font-medium hover:bg-gray-500 transition-colors"
              >
                Bye
              </button>
              <button
                onClick={() => setExtrasModal({ show: true, type: 'leg-bye' })}
                className="py-2 bg-gray-400 text-white rounded-xl text-sm font-medium hover:bg-gray-500 transition-colors"
              >
                Leg Bye
              </button>
              <button
                onClick={() => setWicketModal({
                  show: true,
                  dismissalType: null,
                  fielder: null,
                  runOutBatsman: 'striker',
                  runsCompleted: 0
                })}
                className="py-2 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600 transition-colors"
              >
                Wicket
              </button>
              {/* Next to Wicket because that is where the scorer looks when a
                  batsman leaves the field — but it is the opposite of one: no
                  wicket falls and no bowler is credited. */}
              <button
                onClick={() => setRetireModal({
                  show: true,
                  batsman: 'striker',
                  reason: '',
                  note: ''
                })}
                className="py-2 bg-amber-500 text-white rounded-xl text-sm font-medium hover:bg-amber-600 transition-colors"
              >
                Retire
              </button>
            </div>
          </div>
        )}


        {/* Bowling Squad */}
        <div className={`${theme.card} rounded-xl border-2 ${theme.border} overflow-hidden`}>
          <div className={`p-4 ${theme.backgroundSecondary} flex items-center gap-x-3`}>
            <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Circle className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className={`font-semibold ${theme.text}`}>{bowlingTeam?.name} Bowling</p>
              {!currentOver.bowler && (
                <p className="text-xs text-yellow-500">Select bowler</p>
              )}
            </div>
          </div>
          {activeBowlers.length > 0 && (
            <div className={`border-t ${theme.border}`}>
              <table className="w-full text-sm">
                <thead>
                  <tr className={`${theme.backgroundSecondary} border-b ${theme.border}`}>
                    <th className={`text-left py-2 px-3 text-xs font-semibold ${theme.textMuted}`}>Bowler</th>
                    <th className={`text-center py-2 px-2 text-xs font-semibold ${theme.textMuted}`}>O</th>
                    <th className={`text-center py-2 px-2 text-xs font-semibold ${theme.textMuted}`}>M</th>
                    <th className={`text-center py-2 px-2 text-xs font-semibold ${theme.textMuted}`}>R</th>
                    <th className={`text-center py-2 px-2 text-xs font-semibold ${theme.textMuted}`}>W</th>
                    <th className={`text-center py-2 px-2 text-xs font-semibold ${theme.textMuted}`}>Econ</th>
                  </tr>
                </thead>
                <tbody>
                  {activeBowlers.map(player => {
                    const stats = bowlerStats[player.id];
                    const oversBowled = Math.floor((stats?.balls || 0) / 6);
                    const ballsBowled = (stats?.balls || 0) % 6;
                    const totalOversDecimal = oversBowled + (ballsBowled / 6);
                    const economy = totalOversDecimal > 0 ? ((stats?.runs || 0) / totalOversDecimal).toFixed(2) : '-';
                    const hasReachedLimit = oversBowled >= settings.oversPerBowler;
                    const isCurrentBowler = currentOver.bowler?.id === player.id;
                    const isLastOverBowler = lastOverBowler?.id === player.id;
                    // The previous over's bowler cannot bowl this one. That now
                    // includes coming on as a mid-over replacement, which is a
                    // new way to reach this row.
                    const cannotBowlConsecutive = isLastOverBowler && score.overs > 0 &&
                      (!currentOver.bowler || isOverInProgress);
                    const isRuledOut = ruledOutPlayerIds.has(player.id);
                    const canSelect = !inningsCompleteConfirm.show && !hasReachedLimit && !isCurrentBowler && !cannotBowlConsecutive && !isRuledOut;

                    return (
                      <tr
                        key={player.id}
                        onClick={() => handleBowlerSelect(player, canSelect)}
                        className={`border-b transition-colors ${
                          isCurrentBowler ? `${theme.backgroundSecondary} outline outline-2 -outline-offset-1 outline-blue-500` :
                          hasReachedLimit ? `${theme.border} border-b opacity-50` :
                          isRuledOut ? `${theme.border} border-b opacity-50` :
                          cannotBowlConsecutive ? `${theme.border} border-b opacity-50` :
                          canSelect ? `${theme.border} border-b cursor-pointer hover:bg-blue-500/5` :
                          `${theme.border} border-b`
                        }`}
                      >
                        <td className={`py-2.5 px-3`}>
                          <div className="flex items-center gap-x-1.5">
                            <PlayerAvatar player={player} size="xs" />
                            <span className={`font-medium text-sm ${hasReachedLimit || cannotBowlConsecutive || isRuledOut ? theme.textMuted : theme.text}`}>
                              {shortenName(player.name)}
                            </span>
                            {isCurrentBowler && (
                              <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded flex items-center">
                                <span>BW</span>
                              </span>
                            )}
                            {hasReachedLimit && <span className="text-xs text-orange-400">(done)</span>}
                            {cannotBowlConsecutive && !isRuledOut && <span className="text-xs text-yellow-500">(prev)</span>}
                            {isRuledOut && <span className="text-xs text-red-400">(injured)</span>}
                          </div>
                        </td>
                        <td className={`text-center py-2.5 px-2 text-sm ${theme.text}`}>{oversBowled}.{ballsBowled}</td>
                        <td className={`text-center py-2.5 px-2 text-sm ${theme.textMuted}`}>{stats?.maidens || 0}</td>
                        <td className={`text-center py-2.5 px-2 text-sm ${theme.text}`}>{stats?.runs || 0}</td>
                        <td className={`text-center py-2.5 px-2 text-sm font-semibold ${theme.text}`}>{stats?.wickets || 0}</td>
                        <td className={`text-center py-2.5 px-2 text-sm ${theme.textMuted}`}>{economy}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {isScorer && (
                <p className={`text-xs ${theme.textMuted} px-3 py-2 text-center`}>
                  {!currentOver.bowler ? 'Click a bowler to select for this over' : 'Click another bowler to change'}
                </p>
              )}
            </div>
          )}

          {/* Add Bowler section */}
          {(() => {
            const activeBowlerIds = new Set(activeBowlers.map(p => p.id));
            const availableToAdd = bowlingTeam?.players.filter(
              p => !activeBowlerIds.has(p.id) && !ruledOutPlayerIds.has(p.id)
            ) || [];
            if (availableToAdd.length === 0) return null;
            return (
              <div className={`p-3 ${activeBowlers.length > 0 ? `border-t ${theme.border}` : ''}`}>
                <p className={`text-xs font-medium ${theme.textMuted} mb-2`}>Add Bowler:</p>
                <div className="flex flex-wrap gap-2">
                  {availableToAdd.map(player => (
                    <button
                      key={player.id}
                      type="button"
                      onClick={() => {
                        if (!isScorer) return;
                        // Bringing on somebody who has not bowled yet is the
                        // usual way an injury mid-over gets covered, so it goes
                        // through the same confirmation as a swap in the table
                        // rather than round the back of it.
                        //
                        // He is NOT added to the bowling table here: mid-over
                        // the confirmation decides whether he comes on at all,
                        // and adding him up front puts him in the table behind
                        // the still-open dialog — where he stays if the scorer
                        // cancels, listed as a bowler who never bowled.
                        // confirmBowlerChange adds him once it is answered.
                        if (isOverInProgress) {
                          handleBowlerSelect(player, true);
                          return;
                        }
                        setActiveBowlers(prev => (
                          prev.some(p => p.id === player.id) ? prev : [...prev, player]
                        ));
                        if (!currentOver.bowler) handleBowlerSelect(player, true);
                      }}
                      className={`text-xs px-2.5 py-1 rounded-lg border ${theme.border} ${theme.text} ${theme.backgroundSecondary} hover:border-blue-400 hover:text-blue-400 transition-colors cursor-pointer`}
                    >
                      {shortenName(player.name)}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>

            </div>
          )}
        </div>

        {/* Extras Modal */}
        {extrasModal.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`${theme.card} rounded-xl p-4 w-full max-w-sm border ${theme.border}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-bold ${theme.text}`}>
                  {extrasModal.type === 'wide' && 'Wide'}
                  {extrasModal.type === 'no-ball' && 'No Ball'}
                  {extrasModal.type === 'bye' && 'Bye'}
                  {extrasModal.type === 'leg-bye' && 'Leg Bye'}
                </h3>
                <button
                  onClick={() => setExtrasModal({ show: false, type: null })}
                  className={`p-1 rounded ${theme.cardHover}`}
                >
                  <X className={`w-5 h-5 ${theme.text}`} />
                </button>
              </div>

              {/* Wide: Additional runs (all extras) */}
              {extrasModal.type === 'wide' && (
                <div className="space-y-3">
                  <p className={`text-xs ${theme.textMuted}`}>1 run auto + additional runs (all extras)</p>
                  <div className="grid grid-cols-5 gap-2">
                    {[0, 1, 2, 3, 4].map(runs => (
                      <button
                        key={runs}
                        onClick={() => {
                          recordBall('wide', runs);
                          setExtrasModal({ show: false, type: null });
                        }}
                        className={`py-3 rounded-lg font-bold ${theme.backgroundSecondary} ${theme.text} ${theme.cardHover}`}
                      >
                        +{runs}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* No Ball: Batsman hit or extras */}
              {extrasModal.type === 'no-ball' && (
                <div className="space-y-4">
                  <div>
                    <p className={`text-xs ${theme.textMuted} mb-2`}>Batsman hit (runs to batsman + 1 extra)</p>
                    <div className="grid grid-cols-5 gap-2">
                      {[0, 1, 2, 3, 4].map(runs => (
                        <button
                          key={runs}
                          onClick={() => {
                            recordBall('no-ball-bat', runs);
                            setExtrasModal({ show: false, type: null });
                          }}
                          className={`py-2 rounded-lg font-bold ${theme.backgroundSecondary} ${theme.text} ${theme.cardHover}`}
                        >
                          {runs}
                        </button>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <button
                        onClick={() => {
                          recordBall('no-ball-bat', 4);
                          setExtrasModal({ show: false, type: null });
                        }}
                        className="py-2 bg-blue-500 text-white rounded-lg font-bold"
                      >
                        4
                      </button>
                      <button
                        onClick={() => {
                          recordBall('no-ball-bat', 6);
                          setExtrasModal({ show: false, type: null });
                        }}
                        className="py-2 bg-purple-500 text-white rounded-lg font-bold"
                      >
                        6
                      </button>
                    </div>
                  </div>
                  <div className={`border-t ${theme.border} pt-3`}>
                    <p className={`text-xs ${theme.textMuted} mb-2`}>Batsman didn't hit (all extras)</p>
                    <div className="grid grid-cols-5 gap-2">
                      {[0, 1, 2, 3, 4].map(runs => (
                        <button
                          key={runs}
                          onClick={() => {
                            recordBall('no-ball-extra', runs);
                            setExtrasModal({ show: false, type: null });
                          }}
                          className={`py-2 rounded-lg font-bold bg-yellow-500 text-white`}
                        >
                          +{runs}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Bye: Runs as extras */}
              {extrasModal.type === 'bye' && (
                <div className="space-y-3">
                  <p className={`text-xs ${theme.textMuted}`}>Runs as byes (extras)</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 2, 3, 4].map(runs => (
                      <button
                        key={runs}
                        onClick={() => {
                          recordBall('bye', runs);
                          setExtrasModal({ show: false, type: null });
                        }}
                        className={`py-3 rounded-lg font-bold ${theme.backgroundSecondary} ${theme.text} ${theme.cardHover}`}
                      >
                        {runs}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Leg Bye: Runs as extras */}
              {extrasModal.type === 'leg-bye' && (
                <div className="space-y-3">
                  <p className={`text-xs ${theme.textMuted}`}>Runs as leg byes (extras)</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 2, 3, 4].map(runs => (
                      <button
                        key={runs}
                        onClick={() => {
                          recordBall('leg-bye', runs);
                          setExtrasModal({ show: false, type: null });
                        }}
                        className={`py-3 rounded-lg font-bold ${theme.backgroundSecondary} ${theme.text} ${theme.cardHover}`}
                      >
                        {runs}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bowler Change Modal — only reached part-way through an over */}
        {bowlerChangeModal.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`${theme.card} rounded-xl p-4 w-full max-w-sm border ${theme.border} max-h-[90vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-bold ${theme.text}`}>Change bowler mid-over?</h3>
                <button
                  onClick={() => setBowlerChangeModal({ show: false, incoming: null, reason: '', note: '' })}
                  className={`p-1 rounded ${theme.cardHover}`}
                >
                  <X className={`w-5 h-5 ${theme.text}`} />
                </button>
              </div>

              <div className="space-y-3">
                <p className={`text-sm ${theme.text}`}>
                  <span className="font-semibold">{shortenName(currentOver.bowler?.name)}</span> has bowled{' '}
                  {score.balls} of 6 balls this over.{' '}
                  <span className="font-semibold">{shortenName(bowlerChangeModal.incoming?.name)}</span> will
                  finish it.
                </p>

                <div>
                  <label className={`block text-xs font-medium ${theme.textMuted} mb-1.5`}>
                    Reason for the change
                  </label>
                  <select
                    value={bowlerChangeModal.reason}
                    onChange={(e) => setBowlerChangeModal(prev => ({ ...prev, reason: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border ${theme.border} ${theme.backgroundSecondary} ${theme.text} text-sm`}
                  >
                    <option value="">Select a reason…</option>
                    {PLAYER_CHANGE_REASONS.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>

                {bowlerChangeModal.reason === 'other' && (
                  <input
                    type="text"
                    maxLength={255}
                    autoFocus
                    value={bowlerChangeModal.note}
                    onChange={(e) => setBowlerChangeModal(prev => ({ ...prev, note: e.target.value }))}
                    placeholder="What happened? (optional)"
                    className={`w-full px-3 py-2 rounded-lg border ${theme.border} ${theme.backgroundSecondary} ${theme.text} text-sm`}
                  />
                )}

                {bowlerChangeModal.reason === 'injury_match' && (
                  <p className="text-xs text-amber-500">
                    {shortenName(currentOver.bowler?.name)} will not be available to bowl or bat again in
                    this match.
                  </p>
                )}

                <div className="grid grid-cols-2 gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setBowlerChangeModal({ show: false, incoming: null, reason: '', note: '' })}
                    className={`py-2 rounded-xl border ${theme.border} ${theme.text} text-sm font-medium ${theme.cardHover} transition-colors`}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmBowlerChange}
                    disabled={!bowlerChangeModal.reason}
                    className={`py-2 rounded-xl text-sm font-medium text-white transition-colors ${
                      bowlerChangeModal.reason
                        ? 'bg-blue-500 hover:bg-blue-600 cursor-pointer'
                        : 'bg-blue-500/40 cursor-not-allowed'
                    }`}
                  >
                    Change bowler
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Retire Hurt Modal */}
        {retireModal.show && (() => {
          const retiring = retireModal.batsman === 'nonStriker'
            ? currentOver.nonStriker
            : currentOver.striker;
          const retiringStats = batsmenStats[retiring?.id];
          const end = (which, player, label) => (
            <button
              type="button"
              onClick={() => setRetireModal(prev => ({ ...prev, batsman: which }))}
              className={`p-3 rounded-lg text-center ${theme.backgroundSecondary} ${theme.text} ${theme.cardHover} border-2 ${
                retireModal.batsman === which ? 'border-amber-500' : theme.border
              }`}
            >
              <p className="font-medium">{shortenName(player?.name)}</p>
              <p className={`text-xs ${theme.textMuted}`}>{label}</p>
            </button>
          );

          return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`${theme.card} rounded-xl p-4 w-full max-w-sm border ${theme.border} max-h-[90vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-bold ${theme.text}`}>Retire hurt</h3>
                <button
                  onClick={() => setRetireModal({ show: false, batsman: 'striker', reason: '', note: '' })}
                  className={`p-1 rounded ${theme.cardHover}`}
                >
                  <X className={`w-5 h-5 ${theme.text}`} />
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <p className={`text-sm font-medium ${theme.text} mb-2`}>Who is leaving the field?</p>
                  <div className="grid grid-cols-2 gap-2">
                    {end('striker', currentOver.striker, 'Striker')}
                    {end('nonStriker', currentOver.nonStriker, 'Non-Striker')}
                  </div>
                </div>

                <p className={`text-sm ${theme.text}`}>
                  <span className="font-semibold">{shortenName(retiring?.name)}</span> leaves the field on{' '}
                  {retiringStats?.runs ?? 0}({retiringStats?.balls ?? 0}). This is not a wicket — his
                  innings stays open and no bowler is credited.
                </p>

                <div>
                  <label className={`block text-xs font-medium ${theme.textMuted} mb-1.5`}>
                    Reason for retiring
                  </label>
                  <select
                    value={retireModal.reason}
                    onChange={(e) => setRetireModal(prev => ({ ...prev, reason: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border ${theme.border} ${theme.backgroundSecondary} ${theme.text} text-sm`}
                  >
                    <option value="">Select a reason…</option>
                    {PLAYER_CHANGE_REASONS.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>

                {retireModal.reason === 'other' && (
                  <input
                    type="text"
                    maxLength={255}
                    autoFocus
                    value={retireModal.note}
                    onChange={(e) => setRetireModal(prev => ({ ...prev, note: e.target.value }))}
                    placeholder="What happened? (optional)"
                    className={`w-full px-3 py-2 rounded-lg border ${theme.border} ${theme.backgroundSecondary} ${theme.text} text-sm`}
                  />
                )}

                <p className="text-xs text-amber-500">
                  {retireModal.reason === 'injury_match'
                    ? `${shortenName(retiring?.name)} will not be available to bat or bowl again in this match.`
                    : `${shortenName(retiring?.name)} can be sent back out to bat later in this innings.`}
                </p>

                <div className="grid grid-cols-2 gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setRetireModal({ show: false, batsman: 'striker', reason: '', note: '' })}
                    className={`py-2 rounded-xl border ${theme.border} ${theme.text} text-sm font-medium ${theme.cardHover} transition-colors`}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmRetire}
                    disabled={!retireModal.reason || !retiring}
                    className={`py-2 rounded-xl text-sm font-medium text-white transition-colors ${
                      retireModal.reason && retiring
                        ? 'bg-amber-500 hover:bg-amber-600 cursor-pointer'
                        : 'bg-amber-500/40 cursor-not-allowed'
                    }`}
                  >
                    Retire hurt
                  </button>
                </div>
              </div>
            </div>
          </div>
          );
        })()}

        {/* Wicket Modal */}
        {wicketModal.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`${theme.card} rounded-xl p-4 w-full max-w-sm border ${theme.border} max-h-[90vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-bold ${theme.text}`}>Wicket</h3>
                <button
                  onClick={() => setWicketModal({ show: false, dismissalType: null, fielder: null, runOutBatsman: 'striker', runsCompleted: 0, runOutEnd: null })}
                  className={`p-1 rounded ${theme.cardHover}`}
                >
                  <X className={`w-5 h-5 ${theme.text}`} />
                </button>
              </div>

              {/* Step 1: Select dismissal type */}
              {!wicketModal.dismissalType && (
                <div className="space-y-3">
                  <p className={`text-xs ${theme.textMuted}`}>How was the batsman out?</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { type: 'bowled', label: 'Bowled', needsFielder: false },
                      { type: 'caught', label: 'Caught', needsFielder: true },
                      { type: 'stumped', label: 'Stumped', needsFielder: true },
                      { type: 'run-out', label: 'Run Out', needsFielder: true },
                      { type: 'lbw', label: 'LBW', needsFielder: false },
                      { type: 'hit-wicket', label: 'Hit Wicket', needsFielder: false },
                    ].map(d => (
                      <button
                        key={d.type}
                        onClick={() => {
                          if (d.needsFielder || d.type === 'run-out') {
                            setWicketModal(prev => ({ ...prev, dismissalType: d.type }));
                          } else {
                            // No fielder needed, record directly
                            recordBall('wicket-detail', {
                              dismissalType: d.type,
                              fielder: null,
                              runOutBatsman: 'striker',
                              runsCompleted: 0
                            });
                            setWicketModal({ show: false, dismissalType: null, fielder: null, runOutBatsman: 'striker', runsCompleted: 0, runOutEnd: null });
                          }
                        }}
                        className={`py-3 rounded-lg font-medium ${theme.backgroundSecondary} ${theme.text} ${theme.cardHover} border ${theme.border}`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2a: Caught/Stumped - Select fielder */}
              {(wicketModal.dismissalType === 'caught' || wicketModal.dismissalType === 'stumped') && (
                <div className="space-y-3">
                  <button
                    onClick={() => setWicketModal(prev => ({ ...prev, dismissalType: null }))}
                    className={`flex items-center gap-x-1 text-xs ${theme.textMuted}`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>Back</span>
                  </button>
                  <p className={`text-sm font-medium ${theme.text}`}>
                    {wicketModal.dismissalType === 'caught' ? 'Who took the catch?' : 'Who stumped?'}
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {bowlingTeam?.players.map(player => (
                      <button
                        key={player.id}
                        onClick={() => {
                          recordBall('wicket-detail', {
                            dismissalType: wicketModal.dismissalType,
                            fielder: player,
                            runOutBatsman: 'striker',
                            runsCompleted: 0
                          });
                          setWicketModal({ show: false, dismissalType: null, fielder: null, runOutBatsman: 'striker', runsCompleted: 0, runOutEnd: null });
                        }}
                        className={`w-full p-2 rounded-lg text-left ${theme.backgroundSecondary} ${theme.text} ${theme.cardHover} border ${theme.border}`}
                      >
                        {shortenName(player.name)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2b: Run Out - Multi-step */}
              {wicketModal.dismissalType === 'run-out' && !wicketModal.fielder && (
                <div className="space-y-3">
                  <button
                    onClick={() => setWicketModal(prev => ({ ...prev, dismissalType: null }))}
                    className={`flex items-center gap-x-1 text-xs ${theme.textMuted}`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>Back</span>
                  </button>
                  <p className={`text-sm font-medium ${theme.text}`}>Who got run out?</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setWicketModal(prev => ({ ...prev, runOutBatsman: 'striker', fielder: 'pending' }))}
                      className={`p-3 rounded-lg text-center ${theme.backgroundSecondary} ${theme.text} ${theme.cardHover} border-2 ${
                        wicketModal.runOutBatsman === 'striker' ? 'border-red-500' : theme.border
                      }`}
                    >
                      <p className="font-medium">{shortenName(currentOver.striker?.name)}</p>
                      <p className={`text-xs ${theme.textMuted}`}>Striker</p>
                    </button>
                    <button
                      onClick={() => setWicketModal(prev => ({ ...prev, runOutBatsman: 'nonStriker', fielder: 'pending' }))}
                      className={`p-3 rounded-lg text-center ${theme.backgroundSecondary} ${theme.text} ${theme.cardHover} border-2 ${
                        wicketModal.runOutBatsman === 'nonStriker' ? 'border-red-500' : theme.border
                      }`}
                    >
                      <p className="font-medium">{shortenName(currentOver.nonStriker?.name)}</p>
                      <p className={`text-xs ${theme.textMuted}`}>Non-Striker</p>
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Run Out - Runs completed */}
              {wicketModal.dismissalType === 'run-out' && wicketModal.fielder === 'pending' && (
                <div className="space-y-3">
                  <button
                    onClick={() => setWicketModal(prev => ({ ...prev, fielder: null }))}
                    className={`flex items-center gap-x-1 text-xs ${theme.textMuted}`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>Back</span>
                  </button>
                  <p className={`text-sm font-medium ${theme.text}`}>Runs completed before run out?</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[0, 1, 2, 3].map(runs => {
                      // Check if this is the last wicket (team will be all out after this)
                      const isLastWicket = score.wickets >= (battingTeam?.players?.length || 11) - 2;
                      return (
                        <button
                          key={runs}
                          onClick={() => setWicketModal(prev => ({
                            ...prev,
                            runsCompleted: runs,
                            // Skip "which end" step for last wicket - no new batsman coming
                            fielder: isLastWicket ? 'selectFielder' : 'selectEnd'
                          }))}
                          className={`py-3 rounded-lg font-bold ${theme.backgroundSecondary} ${theme.text} ${theme.cardHover}`}
                        >
                          {runs}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 3b: Run Out - Which end was the run out? */}
              {wicketModal.dismissalType === 'run-out' && wicketModal.fielder === 'selectEnd' && (
                <div className="space-y-3">
                  <button
                    onClick={() => setWicketModal(prev => ({ ...prev, fielder: 'pending' }))}
                    className={`flex items-center gap-x-1 text-xs ${theme.textMuted}`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>Back</span>
                  </button>
                  <p className={`text-sm font-medium ${theme.text}`}>At which end was the run out?</p>
                  <p className={`text-xs ${theme.textMuted}`}>This determines where the new batsman comes in</p>
                  <div className="grid grid-cols-1 gap-2">
                    <button
                      onClick={() => setWicketModal(prev => ({ ...prev, runOutEnd: 'striker-end', fielder: 'selectFielder' }))}
                      className={`p-3 rounded-lg text-left ${theme.backgroundSecondary} ${theme.text} ${theme.cardHover} border ${theme.border}`}
                    >
                      <p className="font-medium">Striker's End (Batting Crease)</p>
                      <p className={`text-xs ${theme.textMuted}`}>New batsman will be striker</p>
                    </button>
                    <button
                      onClick={() => setWicketModal(prev => ({ ...prev, runOutEnd: 'bowler-end', fielder: 'selectFielder' }))}
                      className={`p-3 rounded-lg text-left ${theme.backgroundSecondary} ${theme.text} ${theme.cardHover} border ${theme.border}`}
                    >
                      <p className="font-medium">Bowler's End (Non-striker Crease)</p>
                      <p className={`text-xs ${theme.textMuted}`}>New batsman will be non-striker</p>
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4: Run Out - Select fielder */}
              {wicketModal.dismissalType === 'run-out' && wicketModal.fielder === 'selectFielder' && (
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      // Check if this is the last wicket - go back to 'pending' instead of 'selectEnd'
                      const isLastWicket = score.wickets >= (battingTeam?.players?.length || 11) - 2;
                      setWicketModal(prev => ({ ...prev, fielder: isLastWicket ? 'pending' : 'selectEnd' }));
                    }}
                    className={`flex items-center gap-x-1 text-xs ${theme.textMuted}`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>Back</span>
                  </button>
                  <p className={`text-sm font-medium ${theme.text}`}>Who caused the run out?</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {bowlingTeam?.players.map(player => (
                      <button
                        key={player.id}
                        onClick={() => {
                          recordBall('wicket-detail', {
                            dismissalType: 'run-out',
                            fielder: player,
                            runOutBatsman: wicketModal.runOutBatsman,
                            runsCompleted: wicketModal.runsCompleted,
                            runOutEnd: wicketModal.runOutEnd
                          });
                          setWicketModal({ show: false, dismissalType: null, fielder: null, runOutBatsman: 'striker', runsCompleted: 0, runOutEnd: null });
                        }}
                        className={`w-full p-2 rounded-lg text-left ${theme.backgroundSecondary} ${theme.text} ${theme.cardHover} border ${theme.border}`}
                      >
                        {shortenName(player.name)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Innings Complete Banner - Shows at bottom when innings is complete */}
        {inningsCompleteConfirm.show && (
          <div className={`p-3 rounded-xl border-2 ${
            inningsCompleteConfirm.reason === 'target-chased' ? 'bg-green-500/20 border-green-500' :
            inningsCompleteConfirm.reason === 'match-tied'    ? 'bg-blue-500/20 border-blue-500' :
            inningsCompleteConfirm.reason === 'target-failed' ? 'bg-red-500/20 border-red-500' :
            inningsCompleteConfirm.reason === 'all-out'       ? 'bg-red-500/20 border-red-500' :
            'bg-yellow-500/20 border-yellow-500'
          }`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  inningsCompleteConfirm.reason === 'target-chased' ? 'bg-green-500/30' :
                  inningsCompleteConfirm.reason === 'match-tied'    ? 'bg-blue-500/30' :
                  inningsCompleteConfirm.reason === 'target-failed' ? 'bg-red-500/30' :
                  inningsCompleteConfirm.reason === 'all-out'       ? 'bg-red-500/30' :
                  'bg-yellow-500/30'
                }`}>
                  {inningsCompleteConfirm.reason === 'target-chased' ? (
                    <Trophy className="w-5 h-5 text-green-500" />
                  ) : inningsCompleteConfirm.reason === 'match-tied' ? (
                    <Check className="w-5 h-5 text-blue-500" />
                  ) : inningsCompleteConfirm.reason === 'target-failed' || inningsCompleteConfirm.reason === 'all-out' ? (
                    <X className="w-5 h-5 text-red-500" />
                  ) : (
                    <Check className="w-5 h-5 text-yellow-500" />
                  )}
                </div>
                <div>
                  <p className={`font-semibold ${theme.text}`}>
                    {inningsCompleteConfirm.reason === 'target-chased' ? 'Target Chased!' :
                     inningsCompleteConfirm.reason === 'match-tied'    ? 'Match Tied!' :
                     inningsCompleteConfirm.reason === 'target-failed' ? 'Target Not Reached!' :
                     inningsCompleteConfirm.reason === 'all-out'       ? 'All Out!' :
                     `${currentInning === 1 ? '1st' : currentInning === 2 ? '2nd' : `${currentInning}th`} Innings Complete!`}
                  </p>
                  <p className={`text-xs ${theme.textMuted}`}>
                    {battingTeam?.name}: {inningsCompleteConfirm.pendingScore?.runs}/{inningsCompleteConfirm.pendingScore?.wickets}
                    {' '}({inningsCompleteConfirm.pendingScore?.overs}.{inningsCompleteConfirm.pendingScore?.balls} ov)
                  </p>
                </div>
              </div>
              {isScorer && (
                <button
                  onClick={() => {
                    setInningsCompleteConfirm({ show: false, reason: null, pendingScore: null, pendingBatsmenStats: null, pendingBowlerStats: null });
                    handleInningsComplete(
                      inningsCompleteConfirm.pendingScore,
                      inningsCompleteConfirm.pendingBatsmenStats,
                      inningsCompleteConfirm.pendingBowlerStats
                    );
                  }}
                  className={`px-4 py-2 ${theme.buttonPrimary} rounded-lg font-semibold transition-colors flex items-center gap-2 whitespace-nowrap`}
                >
                  <span>{innings.length === 0 ? 'Next' : 'Finish'}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

      </div>
    );
  };

  // Render Innings Break
  const renderInningsBreak = () => {
    const lastInnings = innings[innings.length - 1];

    return (
      <div className="text-center space-y-6">
        <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${theme.backgroundSecondary} border-2 border-yellow-500`}>
          <RotateCcw className="w-8 h-8 text-yellow-500" />
        </div>

        <div>
          <h2 className={`text-2xl font-bold ${theme.text}`}>Innings Complete</h2>
          <p className={`text-sm ${theme.textMuted} mt-1`}>
            {lastInnings?.battingTeam?.name} scored {lastInnings?.score.runs}/{lastInnings?.score.wickets}
          </p>
        </div>

        <div className={`${theme.card} rounded-xl p-4 border ${theme.border}`}>
          <p className={`text-lg font-medium ${theme.text}`}>
            {battingTeam?.name} needs {lastInnings?.score.runs + 1} runs to win
          </p>
          <p className={`text-sm ${theme.textMuted}`}>
            {settings.oversPerInning === -1 ? 'Unlimited overs' : `from ${settings.oversPerInning} overs`}
          </p>
        </div>

        {isScorer ? (
          <button
            onClick={async () => {
              // Update status to active for 2nd innings
              if (matchId) {
                try {
                  const updatedMatch = await cricketGameAPI.updateState(matchId, {
                    status: 'in_progress',
                    currentInnings: currentInning
                  });
                  setGame(updatedMatch);
                  setCurrentPhase('scoring');
                } catch (error) {
                  console.error('Failed to update match state:', error);
                  setCurrentPhase('scoring'); // Continue anyway
                }
              } else {
                setCurrentPhase('scoring');
              }
            }}
            className={`w-full py-4 ${theme.buttonPrimary} rounded-xl font-semibold transition-colors flex items-center justify-center gap-x-2`}
          >
            <Play className="w-5 h-5" />
            <span>Start {currentInning === 2 ? '2nd' : `${currentInning}th`} Innings</span>
          </button>
        ) : (
          <p className={`text-center text-sm ${theme.textMuted} py-4`}>Waiting for scorer to start next innings...</p>
        )}
      </div>
    );
  };

  // Reusable function to render batting stats table for an innings
  const renderInningsBattingStats = (inn) => {
    const getPlayer = (playerId) => {
      const id = String(playerId);
      return inn.battingTeam?.players?.find(p => String(p.id) === id);
    };

    const batsmenEntries = Object.entries(inn.batsmenStats || {})
      .filter(([_, stats]) => stats.balls > 0 || stats.runs > 0)
      .map(([id, stats]) => {
        const playerObj = getPlayer(id);
        return {
          id,
          name: shortenName(playerObj?.name) || 'Unknown',
          playerObj,
          ...stats
        };
      })
      .sort((a, b) => (b.runs || 0) - (a.runs || 0));

    // Find players who didn't bat
    const battedPlayerIds = new Set(batsmenEntries.map(b => String(b.id)));
    const didNotBat = (inn.battingTeam?.players || [])
      .filter(p => !battedPlayerIds.has(String(p.id)))
      .map(p => shortenName(p.name));

    if (batsmenEntries.length === 0) {
      return <p className={`text-sm ${theme.textMuted}`}>No batting data</p>;
    }

    return (
      <div className={`overflow-x-auto rounded-lg border-2 ${theme.border}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className={`${theme.backgroundSecondary} border-b-2 ${theme.border}`}>
              <th className={`text-left py-2.5 px-3 ${theme.text} font-semibold`}>Batter</th>
              <th className={`text-center py-2.5 px-2 ${theme.text} font-semibold`}>R</th>
              <th className={`text-center py-2.5 px-2 ${theme.text} font-semibold`}>B</th>
              <th className={`text-center py-2.5 px-2 ${theme.text} font-semibold`}>4s</th>
              <th className={`text-center py-2.5 px-2 ${theme.text} font-semibold`}>6s</th>
              <th className={`text-center py-2.5 px-2 ${theme.text} font-semibold`}>SR</th>
            </tr>
          </thead>
          <tbody>
            {batsmenEntries.map((batter, index) => {
              const strikeRate = batter.balls > 0 ? ((batter.runs / batter.balls) * 100).toFixed(1) : '0.0';
              // Format dismissal description like "c Fielder b Bowler" or "not out"
              const getDismissalText = () => {
                if (batter.out) return formatDismissal(batter.dismissal);
                // Somebody who walked off hurt and never came back was not
                // there at the end, so "not out" would misread the card.
                if (everRetiredIds.has(String(batter.id))) return 'retired hurt';
                return 'not out';
              };
              const dismissalText = getDismissalText();
              const isNotOut = !batter.out;
              const rowBg = index % 2 === 0 ? '' : theme.backgroundSecondary + '/30';
              return (
                <tr key={batter.id} className={`border-b ${theme.border} ${rowBg}`}>
                  <td className={`py-2.5 px-3 ${theme.text}`}>
                    <div className="flex items-start gap-2">
                      <PlayerAvatar player={batter.playerObj} size="xs" />
                      <div className="flex flex-col">
                        <span className="font-medium">{batter.name}</span>
                        <span className={`text-xs ${isNotOut ? theme.textMuted : 'text-red-400'}`}>
                          {dismissalText}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className={`text-center py-2.5 px-2 font-semibold ${theme.text}`}>{batter.runs || 0}</td>
                  <td className={`text-center py-2.5 px-2 ${theme.textMuted}`}>{batter.balls || 0}</td>
                  <td className={`text-center py-2.5 px-2 ${theme.textMuted}`}>{batter.fours || 0}</td>
                  <td className={`text-center py-2.5 px-2 ${theme.textMuted}`}>{batter.sixes || 0}</td>
                  <td className={`text-center py-2.5 px-2 ${theme.textMuted}`}>{strikeRate}</td>
                </tr>
              );
            })}
            {/* Extras Row */}
            {inn.score?.extras && (
              <tr className={`border-b ${theme.border} bg-amber-500/10`}>
                <td className={`py-2.5 px-3 ${theme.text}`}>
                  <span className="font-medium italic">Extras</span>
                </td>
                <td className={`text-center py-2.5 px-2 font-semibold ${theme.text}`}>
                  {(inn.score.extras.wides || 0) + (inn.score.extras.noBalls || 0) + (inn.score.extras.byes || 0) + (inn.score.extras.legByes || 0)}
                </td>
                <td colSpan="4" className={`text-left py-2.5 ${theme.textMuted} text-xs pl-2`}>
                  ({inn.score.extras.noBalls > 0 ? `NB ${inn.score.extras.noBalls}` : ''}
                  {inn.score.extras.wides > 0 ? `${inn.score.extras.noBalls > 0 ? ', ' : ''}W ${inn.score.extras.wides}` : ''}
                  {inn.score.extras.byes > 0 ? `${(inn.score.extras.noBalls > 0 || inn.score.extras.wides > 0) ? ', ' : ''}B ${inn.score.extras.byes}` : ''}
                  {inn.score.extras.legByes > 0 ? `${(inn.score.extras.noBalls > 0 || inn.score.extras.wides > 0 || inn.score.extras.byes > 0) ? ', ' : ''}LB ${inn.score.extras.legByes}` : ''})
                </td>
              </tr>
            )}
            {/* Total Row */}
            <tr className="bg-green-500/20">
              <td className={`py-2.5 px-3 ${theme.text} font-bold`}>Total</td>
              <td className={`text-center py-2.5 px-2 font-bold text-lg ${theme.text}`}>{inn.score?.runs || 0}</td>
              <td colSpan="4" className={`text-left py-2.5 ${theme.textMuted} text-xs pl-2`}>
                ({inn.score?.wickets || 0} wkts, {inn.score?.overs || 0}.{inn.score?.balls || 0} ov)
              </td>
            </tr>
            {/* Did Not Bat Row */}
            {didNotBat.length > 0 && (
              <tr className={`border-t ${theme.border}`}>
                <td colSpan="6" className={`py-2.5 px-3 ${theme.textMuted} text-xs`}>
                  <span className="font-medium">Did not bat: </span>
                  {didNotBat.join(', ')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Reusable function to render bowling stats table for an innings
  const renderInningsBowlingStats = (inn) => {
    const getBowler = (playerId) => {
      const id = String(playerId);
      return inn.bowlingTeam?.players?.find(p => String(p.id) === id);
    };

    const bowlerEntries = Object.entries(inn.bowlerStats || {})
      .filter(([_, stats]) => stats.balls > 0)
      .map(([id, stats]) => {
        const playerObj = getBowler(id);
        return {
          id,
          name: shortenName(playerObj?.name) || 'Unknown',
          playerObj,
          ...stats
        };
      })
      .sort((a, b) => (b.wickets || 0) - (a.wickets || 0) || (a.runs || 0) - (b.runs || 0));

    if (bowlerEntries.length === 0) {
      return <p className={`text-sm ${theme.textMuted}`}>No bowling data</p>;
    }

    return (
      <div className={`overflow-x-auto rounded-lg border-2 ${theme.border}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className={`${theme.backgroundSecondary} border-b-2 ${theme.border}`}>
              <th className={`text-left py-2.5 px-3 ${theme.text} font-semibold`}>Bowler</th>
              <th className={`text-center py-2.5 px-2 ${theme.text} font-semibold`}>O</th>
              <th className={`text-center py-2.5 px-2 ${theme.text} font-semibold`}>M</th>
              <th className={`text-center py-2.5 px-2 ${theme.text} font-semibold`}>R</th>
              <th className={`text-center py-2.5 px-2 ${theme.text} font-semibold`}>W</th>
              <th className={`text-center py-2.5 px-2 ${theme.text} font-semibold`}>Econ</th>
            </tr>
          </thead>
          <tbody>
            {bowlerEntries.map((bowler, index) => {
              const overs = Math.floor((bowler.balls || 0) / 6);
              const balls = (bowler.balls || 0) % 6;
              const totalOvers = overs + (balls / 6);
              const economy = totalOvers > 0 ? ((bowler.runs || 0) / totalOvers).toFixed(2) : '0.00';
              const rowBg = index % 2 === 0 ? '' : theme.backgroundSecondary + '/30';
              return (
                <tr key={bowler.id} className={`border-b ${theme.border} ${rowBg}`}>
                  <td className={`py-2.5 px-3 ${theme.text}`}>
                    <div className="flex items-center gap-2">
                      <PlayerAvatar player={bowler.playerObj} size="xs" />
                      <span className="font-medium">{bowler.name}</span>
                    </div>
                  </td>
                  <td className={`text-center py-2.5 px-2 ${theme.textMuted}`}>{overs}.{balls}</td>
                  <td className={`text-center py-2.5 px-2 ${theme.textMuted}`}>{bowler.maidens || 0}</td>
                  <td className={`text-center py-2.5 px-2 ${theme.textMuted}`}>{bowler.runs || 0}</td>
                  <td className={`text-center py-2.5 px-2 font-semibold ${theme.text}`}>{bowler.wickets || 0}</td>
                  <td className={`text-center py-2.5 px-2 ${theme.textMuted}`}>{economy}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Render Match Complete
  const renderCompleted = () => {

    const firstInnings = innings[0];
    const secondInnings = innings[1];

    let winner = null;
    let margin = '';

    if (secondInnings) {
      // Use actual team size for wickets calculation
      const secondInningsTeamSize = secondInnings.battingTeam?.players?.length || settings.maxPlayersPerTeam;
      const wicketsRemaining = (secondInningsTeamSize - 1) - secondInnings.score.wickets;

      if (secondInnings.score.runs > firstInnings.score.runs) {
        winner = secondInnings.battingTeam?.name || 'Team 2';
        margin = `by ${wicketsRemaining} wicket${wicketsRemaining !== 1 ? 's' : ''}`;
      } else if (firstInnings.score.runs > secondInnings.score.runs) {
        winner = firstInnings.battingTeam?.name || 'Team 1';
        const runMargin = firstInnings.score.runs - secondInnings.score.runs;
        margin = `by ${runMargin} run${runMargin !== 1 ? 's' : ''}`;
      } else {
        margin = 'Match Tied!';
      }
    }

    // Toggle innings expansion
    const toggleInnings = (idx) => {
      setExpandedInnings(prev => ({
        ...prev,
        [idx]: !prev[idx]
      }));
    };

    return (
      <div className="space-y-6">
        {/* Match Summary - Side by Side */}
        <div className={`${theme.card} rounded-xl border ${theme.border} p-4`}>
          <div className="flex items-center justify-between">
            {/* Team 1 (1st Innings) */}
            <div className="flex-1 text-center">
              <div className="w-12 h-12 mx-auto rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600 mb-2 shadow-md">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <p className={`font-semibold ${theme.text} text-sm`}>
                {firstInnings?.battingTeam?.name || 'Team 1'}
              </p>
            </div>

            {/* Scores */}
            <div className="flex-1 flex items-center justify-center gap-x-4">
              {/* Team 1 Score */}
              <div className="text-center">
                <p className={`text-2xl font-bold ${theme.text}`}>
                  {firstInnings?.score?.runs || 0}/{firstInnings?.score?.wickets || 0}
                </p>
                <p className={`text-xs ${theme.textMuted}`}>
                  ({firstInnings?.score?.overs || 0}.{firstInnings?.score?.balls || 0})
                </p>
              </div>

              {/* VS Divider */}
              <div className={`text-sm ${theme.textMuted} px-2`}>-</div>

              {/* Team 2 Score */}
              <div className="text-center">
                <p className={`text-2xl font-bold ${theme.text}`}>
                  {secondInnings?.score?.runs || 0}/{secondInnings?.score?.wickets || 0}
                </p>
                <p className={`text-xs ${theme.textMuted}`}>
                  ({secondInnings?.score?.overs || 0}.{secondInnings?.score?.balls || 0})
                </p>
              </div>
            </div>

            {/* Team 2 (2nd Innings) */}
            <div className="flex-1 text-center">
              <div className="w-12 h-12 mx-auto rounded-lg flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 mb-2 shadow-md">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <p className={`font-semibold ${theme.text} text-sm`}>
                {secondInnings?.battingTeam?.name || 'Team 2'}
              </p>
            </div>
          </div>

          {/* Result Text */}
          <div className={`text-center mt-4 pt-4 border-t border-dashed ${theme.border}`}>
            <p className={`font-semibold ${theme.text}`}>
              {winner ? `${winner} won ${margin}` : 'Match Tied!'}
            </p>
          </div>
        </div>

        {/* Innings Details - Expandable */}
        <div className="space-y-4">
          {innings.map((inn, idx) => {
            const isExpanded = expandedInnings[idx];
            const ordinal = idx === 0 ? '1st' : idx === 1 ? '2nd' : `${idx + 1}th`;

            return (
              <div key={idx} className={`${theme.card} rounded-xl border ${theme.border} overflow-hidden`}>
                {/* Innings Header - Clickable */}
                <button
                  type="button"
                  onClick={() => toggleInnings(idx)}
                  className={`w-full p-4 flex items-center justify-between ${theme.cardHover} transition-colors`}
                >
                  <div className="flex items-center gap-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${theme.backgroundSecondary} border ${theme.border}`}>
                      <span className={`text-xs font-bold ${theme.text}`}>{idx + 1}</span>
                    </div>
                    <div className="text-left">
                      <p className={`font-semibold ${theme.text}`}>{inn.battingTeam?.name || 'Team'}</p>
                      <p className={`text-xs ${theme.textMuted}`}>{ordinal} Innings</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-x-3">
                    <div className="text-right">
                      <p className={`text-xl font-bold ${theme.text}`}>
                        {inn.score.runs}/{inn.score.wickets}
                      </p>
                      <p className={`text-xs ${theme.textMuted}`}>
                        ({inn.score.overs}.{inn.score.balls} ov)
                      </p>
                    </div>
                    <ChevronDown className={`w-5 h-5 ${theme.textMuted} transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {/* Expanded Stats */}
                {isExpanded && (
                  <div className={`border-t ${theme.border} p-4 space-y-4`}>
                    {/* Batting */}
                    <div>
                      <h4 className={`text-sm font-semibold ${theme.text} mb-2 flex items-center gap-x-2`}>
                        <Target className="w-4 h-4 text-green-500" />
                        <span>Batting</span>
                      </h4>
                      {renderInningsBattingStats(inn)}
                    </div>

                    {/* Bowling */}
                    <div>
                      <h4 className={`text-sm font-semibold ${theme.text} mb-2 flex items-center gap-x-2`}>
                        <Circle className="w-4 h-4 text-blue-500" />
                        <span>Bowling ({inn.bowlingTeam?.name || 'Team'})</span>
                      </h4>
                      {renderInningsBowlingStats(inn)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          {(() => {
            return null;
          })()}
          {isScorer && onGameComplete && event?.status !== 'completed' && (
            <button
              onClick={() => {
                onGameComplete({
                  winner,
                  margin,
                  innings,
                  matchId
                });
              }}
              className={`flex-1 py-3 px-6 ${theme.buttonPrimary} rounded-xl font-semibold transition-colors flex items-center justify-center gap-2`}
            >
              <Check className="w-5 h-5" />
              <span>Complete Event & Continue</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`${theme.card} rounded-2xl border ${theme.border} p-6 relative`}>
      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center z-50">
          <div className="flex items-center gap-x-2 text-white">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Loading...</span>
          </div>
        </div>
      )}

      {/* API Error Banner */}
      {apiError && (
        <div className="mb-4 p-3 bg-yellow-500/20 border border-yellow-500 rounded-lg flex items-center justify-between">
          <span className="text-yellow-500 text-sm">{apiError}</span>
          <button
            onClick={() => setApiError(null)}
            className="text-yellow-500 hover:text-yellow-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Viewer banner */}
      {!isScorer && (
        <div className="mb-4 p-3 bg-amber-500/20 border border-amber-500 rounded-lg flex items-center gap-2">
          <span className="text-amber-500 text-sm font-medium">Watching live — scores update automatically</span>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div>
          <h2 className={`text-2xl font-bold ${theme.text} mb-1`}>
            Cricket - {event?.subject || event?.name || 'Match'}
          </h2>
          <div className="flex items-center gap-2">
            <p className={`text-sm ${theme.textMuted}`}>
              {currentPhase === 'scoring' && `${battingTeam?.name || 'Team'} batting`}
              {currentPhase === 'innings-break' && 'Innings break'}
              {currentPhase === 'completed' && 'Match completed'}
            </p>
            {currentPhase === 'scoring' && !paused && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                LIVE
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Phase Content */}
      {currentPhase === 'scoring' && renderScoring()}
      {currentPhase === 'innings-break' && renderInningsBreak()}
      {currentPhase === 'completed' && renderCompleted()}

      {/* Toss Modal */}
      <CoinToss
        isOpen={showToss}
        onClose={() => setShowToss(false)}
        onTossComplete={handleTossComplete}
        teamA={{ id: 'teamA', name: teamA.name }}
        teamB={{ id: 'teamB', name: teamB.name }}
        choices={['Bat First', 'Field First']}
        title="Cricket Toss"
      />

    </div>
  );
};

export default CricketScoring;
